/**
 * Demo DB の型定義のみを持つモジュール。
 *
 * 元は `schema.ts` に DemoDatabase クラス（Dexie + EntityTable<T> × 25 + migration version × 16）
 * と全ての型定義（700 行超）が同居していたため、route -api / -query / components が
 * `import type { Foo } from "@/mocks/db/schema"` するだけで Dexie generic Table 型 + 全 EntityTable
 * の instantiation を tsgo に評価させ、型相関 Map を圧迫していた。
 *
 * このモジュールは型のみを export し、DemoDatabase クラスは見えない。
 * 値（`db`）が必要な mock repository 層だけが `schema.ts` を import する設計に寄せる。
 */

import type { IconName } from '@/components/Icon/iconMap';
import type { WorkerType } from '@/constants/workerType';
import type { StructureCellRole, StructureSubstageFilterRule } from '@/features/engine-shared/structure-detail/types';
// clause UI（WorkerTypeRuleCard / SetMembership / SetIndividually）へそのまま渡せる形で
// mock を持つため、people-group API 由来の型を借用する（型のみ・実行時依存なし）
import type { Membership as PeopleGroupMembership } from '@/features/people-group/-api';
import type { Worker as PeopleGroupWorker } from '@/features/worker-type-rule/types';
import type { OperationEventSnapshotRow } from './operationEventPayload';

// ─── Existing ───────────────────────────────────────────────

export interface Person {
  id: string;
  name: string;
  department: string;
}

// ─── Schema-local row type (decoupled from feature UI types) ─

export interface SchemaTableColumn {
  largeCategoryCode: string;
  largeCategoryName: string;
  mediumCategoryCode: string;
  mediumCategoryName: string;
  inputFieldCount: number;
  requiredFieldCount: number;
  hasIncludeFile: boolean;
}
export interface SchemaTableTarget {
  targetType: string;
  targetId: string;
  targetName: string;
  targetAvatarUrl?: string;
}
export interface SchemaTableRow {
  id: string;
  completedOperationStepCount: number;
  totalOperationStepCount: number;
  target: SchemaTableTarget;
  anchorDate: string;
  columns: SchemaTableColumn[];
  /** 任意の属性テキスト列（AggregateTable の attributeColumns で表示）。 */
  attributes?: Record<string, string>;
}

// ─── Attendance Domain ──────────────────────────────────────

export type AttendanceRecordStatus = 'present' | 'absent' | 'late' | 'early_leave' | 'holiday' | 'paid_leave' | 'sick_leave' | 'non_working_day';
export type AttendanceApprovalStatus = 'pending' | 'approved' | 'rejected';
export type ClockingRecordType = 'clock_in' | 'clock_out' | 'break_start' | 'break_end';
export type ClockingRecordMethod = 'web' | 'mobile' | 'ic_card' | 'biometric' | 'manual';
export type AttendanceAlertType = 'overtime_limit' | 'no_clock_in' | 'no_clock_out' | 'consecutive_overtime' | 'interval_violation' | 'monthly_80h_exceeded' | 'annual_360h_exceeded';
export type AlertSeverity = 'info' | 'warning' | 'critical';
export type AttendanceEmployeeStatus = 'active' | 'on_leave' | 'retired';
export interface AttendanceRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  date: string;
  scheduledStart: string;
  scheduledEnd: string;
  actualStart: string | null;
  actualEnd: string | null;
  status: AttendanceRecordStatus;
  overtimeMinutes: number;
  lateMinutes: number;
  memo: string;
  workplaceName: string;
  approvalStatus: AttendanceApprovalStatus;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}
export type TimeClockCorrectionStatus = 'pending' | 'approved' | 'rejected';

/**
 * 打刻の修正（だこくのしゅうせい / Time Clock Correction）の一括申請レコード。
 * 従業員が日次勤怠の出勤・退勤・休憩を修正し、人事承認のために申請したもの（デモ用 Dexie のみ）。
 */
export interface TimeClockCorrection {
  id: string;
  employeeId: string;
  organizationId: string;
  /** 対象日 YYYY-MM-DD */
  date: string;
  /** 申請単位（同一一括申請でまとめる） */
  batchId: string;
  originalClockIn: string | null;
  originalClockOut: string | null;
  originalBreakMinutes: number;
  correctedClockIn: string | null;
  correctedClockOut: string | null;
  correctedBreakMinutes: number;
  reason: string;
  status: TimeClockCorrectionStatus;
  createdAt: string;
}
export interface ClockingRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  timestamp: string;
  type: ClockingRecordType;
  method: ClockingRecordMethod;
  locationName: string | null;
  latitude: number | null;
  longitude: number | null;
  deviceId: string | null;
  verified: boolean;
  organizationId: string;
  createdAt: string;
}
export interface AttendanceAlert {
  id: string;
  employeeId: string;
  employeeName: string;
  type: AttendanceAlertType;
  severity: AlertSeverity;
  message: string;
  date: string;
  resolved: boolean;
  resolvedAt: string | null;
  organizationId: string;
  createdAt: string;
}
export interface AttendanceEmployee {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  attendanceStructureId: string;
  attendanceStructureName: string;
  attendanceManagerId: string | null;
  attendanceManagerName: string | null;
  monthlyWorkDays: number;
  monthlyOvertimeHours: number;
  paidLeaveRemaining: number;
  paidLeaveTotal: number;
  status: AttendanceEmployeeStatus;
  organizationId: string;
}

// ─── Payroll Domain ─────────────────────────────────────────

export type PaySlipStatus = 'draft' | 'calculating' | 'pending_approval' | 'approved' | 'paid' | 'cancelled';
export type PaySlipRecordCategory = 'earning' | 'deduction' | 'tax' | 'social_insurance' | 'net_pay';
export interface PaySlip {
  id: string;
  title: string;
  period: string;
  paymentDate: string;
  status: PaySlipStatus;
  employeeCount: number;
  totalGrossAmount: number;
  totalNetAmount: number;
  totalDeductions: number;
  currency: string;
  countryCode: string;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
export interface PaySlipRecord {
  id: string;
  paySlipId: string;
  employeeId: string;
  employeeName: string;
  department: string;
  category: PaySlipRecordCategory;
  itemName: string;
  itemCode: string;
  amount: number;
  currency: string;
  description: string;
  sortOrder: number;
  createdAt: string;
}
export interface PaySlipFolder {
  id: string;
  paySlipId: string;
  name: string;
  description: string;
  fileCount: number;
  createdAt: string;
  updatedAt: string;
}
export interface PaySlipFile {
  id: string;
  paySlipId: string;
  folderId: string;
  name: string;
  mimeType: string;
  size: number;
  url: string;
  uploadedBy: string;
  createdAt: string;
}
export interface EmployeePaySlipSummary {
  id: string;
  employeeId: string;
  employeeName: string;
  email: string;
  avatarUrl: string | null;
  organization: string;
  period: string;
  grossAmount: number;
  prevGrossAmount: number | null;
  totalDeductions: number;
  prevTotalDeductions: number | null;
  netAmount: number;
  prevNetAmount: number | null;
  overtimeHours: string;
  operationActivity: string | null;
  organizationId: string;
}
export interface EmployeePayslipAttendance {
  id: string;
  employeeId: string;
  period: string;
  organizationId: string;
  scheduledWorkDays: number;
  attendanceDays: number;
  absenceDays: number;
  holidayWorkDays: number;
  paidLeaveUsedDays: number;
  paidLeaveRemainingDays: number;
  workHours: number;
  overtimeHours: number;
  nightOvertimeHours: number;
  holidayWorkHours: number;
  lateEarlyHours: number;
}
export type PaySlipCalculationCategoryId = 'attendance' | 'earnings' | 'social_insurance' | 'tax' | 'result';
export interface PaySlipPayItemLine {
  lineKey: string;
  label: string;
  valueText: string;
  valueUnit?: string | null;
}
export interface PaySlipCalculationSnapshotRow {
  categoryId: PaySlipCalculationCategoryId;
  variableKey: string;
  label: string;
  valueText: string;
  valueUnit?: string | null;
  lines?: PaySlipPayItemLine[] | null;
}
export interface PaySlipCalculationSnapshot {
  id: string;
  employeeId: string;
  period: string;
  snapshotAt: string;
  title: string;
  rows: PaySlipCalculationSnapshotRow[];
  organizationId: string;
}

// ─── Engine Domain ──────────────────────────────────────────

export type EngineLifecycleStatus = 'draft' | 'applied' | 'archived';

/** Logic component の表示用 glyph 種別 (LogicComponentCard の見た目を決める)。 */
/**
 * Logic Component の種別。カードの glyph とサイドタブ Unit 行のアイコンを決める
 * （`features/engine-shared/structure-detail/logicComponentIcon.ts`）。
 *
 * 四則演算を `arithmetic` 1 つに畳んでいたが、足し算と割り算が同じ絵で出ると
 * 「その unit が何をする計算か」が行から読めないため演算子レベルに割った。
 */
export type LogicComponentGlyphKind = 'branch' | 'tableLookup' | 'split' | 'rounding' | 'sum' | 'average' | 'min' | 'max' | 'add' | 'subtract' | 'multiply' | 'divide' | 'prorate' | 'lookup' | 'switch';

/** Payroll item が扱う値の型。表示フォーマットと計量単位の種類を決める。 */
export type PayrollValueType = 'quantity' | 'coefficient' | 'money' | 'table' | 'enumeration' | 'date' | 'time' | 'duration' | 'address';

/** 時間量・金額の分母に使う時間単位。 */
export type PayrollDurationUnit = 'second' | 'minute' | 'hour' | 'day';

/** 計量単位。valueType に応じて kind が決まる。
 *  money: 通貨 + 任意の時間分母（JPY, JPY/hour 等） / duration: 時間単位 /
 *  quantity: 数量単位（日・回 等） / text: その他 value type の自由記述単位。 */
export type PayrollUnitOfMeasurement = {
  kind: 'money';
  /** ISO 4217 通貨コード（例: JPY, USD） */
  numerator: {
    code: string;
  };
  denominator?: {
    value: number;
    unit: PayrollDurationUnit;
  };
} | {
  kind: 'duration';
  unit: PayrollDurationUnit;
} | {
  kind: 'quantity';
  unit: string;
} | {
  kind: 'text';
  unit: string;
};

/** Payroll item の type — 給与明細上のロール。
 *  earning: 支給項目 / deduction: 控除項目。
 *  入力データ・マスタ・中間値・帳票出力等は未設定 (undefined)。 */
export type PayrollItemType = 'earning' | 'deduction';

/** Payroll item の category — 帳簿上の分類。
 *  earnings: 支給 — 明細のプラス項目 /
 *  deductions: 控除 — 明細のマイナス項目 /
 *  aggregates: 集計 — 計算で得られる中間値・集計値 /
 *  adjustments: 調整 — 欠勤控除・遅刻早退控除などの支給調整 /
 *  employerContributions: 事業者負担 — 社保の会社負担分・労災保険料など本人手取りに出ない負担 /
 *  reimbursements: 立替経費 — 従業員が立替えた実費の精算（給与ではないが給与と同時支給）/
 *  paymentsInKind: 現物給与 — 金銭以外の形で支給する給与（社宅・食事・通勤定期の現物）/
 *  references: 参照情報 — 計算の入力・マスタ参照 /
 *  inquiry: 照会 — テーブル・料率などの照会。
 *
 *  一覧の side panel は 8 枠で、`references` の枠が `references` と `inquiry` の
 *  両方を拾う。`inquiry` を残しているのは、追加フローの「照会先」必須判定と
 *  対応表カードの表示切替がこの区別に依存しているため
 *  （`payrollItemAddDraft` / `PayrollItemLookupTableCard`）。 */
export type PayrollItemCategory = 'earnings' | 'deductions' | 'aggregates' | 'adjustments' | 'employerContributions' | 'reimbursements' | 'paymentsInKind' | 'references' | 'inquiry';

/** Payroll item の provisionBasis — 制定根拠。
 *  statutory: 法定（健保・年金・税等） / in-house: 会社独自（役職手当等）。 */
export type PayrollItemProvisionBasis = 'statutory' | 'in-house';

/** Payroll item の variability — 月次変動性。
 *  fixed: 月毎で変動しない / variable: 月毎で変動する。 */
export type PayrollItemVariability = 'fixed' | 'variable';

/** Payroll item の taxability — 課税対象性。
 *  taxable: 課税 / non-taxable: 非課税 /
 *  partially-taxable: 一部課税（限度額超過分のみ課税の通勤費等） /
 *  conditionally-taxable: 条件付き課税（条件で課税範囲が変わる）。 */
export type PayrollItemTaxability = 'taxable' | 'non-taxable' | 'partially-taxable' | 'conditionally-taxable';

/** 参照項目が値を引く表と、その表から選んだ 1 カラム。 */
export interface PayrollItemReferenceColumn {
  /** 参照先の表 item（`valueType: 'table'` の PayrollItem）の id。 */
  tableItemId: string;
  /** 上記の表から選んだカラムの列見出し。 */
  column: string;
}

/** Payroll engine の item 原子マスタ。
 *  「年俸額」「健康保険料」「標準報酬月額」のような payroll structure / unit から参照される atom。
 *  category / kind union は持たない (engineComponents 時代の遺物を排除)。 */
export interface PayrollItem {
  id: string;
  name: string;
  nameEn: string;
  /** 計算式・帳票から参照するコード。一覧のコード列・詳細 Identity カードに表示する。 */
  code: string;
  description: string;
  countryCode: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  /** JSON.stringify された任意の設定。tablePreview もここに含める。 */
  config: string;
  validFrom: string;
  validTo: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** structure detail カードの badge 文字 (例: "標報", "健保", "年俸") */
  displayBadge?: string;
  /** structure detail カードの badge 種別ラベル (例: "整数", "DEDUCTION・STATUTORY・VARIABLE") */
  displayBadgeKind?: string;
  /** 項目が取る値の型。 */
  valueType: PayrollValueType;
  /** 計量単位。未設定の item は表示上 '—' になる。 */
  unitOfMeasurement?: PayrollUnitOfMeasurement;
  /** 帳簿上の主分類（支給・控除・参照・照会）。項目の同一性を決める軸で、
   *  追加フロー・一括取込 CSV・照会先の要否判定・詳細カードはこの値だけを見る。 */
  category: PayrollItemCategory;
  /** 主分類のほかに、その項目が同時に属する分類。
   *
   *  集計項目のうち「明細の支給欄・控除欄に並ぶ金額の合計や内訳」であるもの
   *  （総支給額・控除合計など）は、集計であると同時に支給・控除でもある。一覧の
   *  分類タブはここまで含めて数え・絞り込むので、支給タブから総支給額を引ける。
   *
   *  主分類と同じ値は入れない。`inquiry` も入れない（照会かどうかが対応表カードと
   *  追加フローの照会先必須判定を切り替えるため、半端に照会扱いになると壊れる）。 */
  additionalCategories?: PayrollItemCategory[];
  /** 管理タグ。一覧・詳細で badge 表示し、フィルタ対象になる。 */
  managementTags?: string[];
  /** 値を引く表と、その表から選んだ 1 カラム。参照テーブル（R048 / R049）や
   *  照会表（号俸表など）のように `valueType: 'table'` の item を
   *  `tableItemId` で名指しし、`column` にその表の列見出しを持つ。 */
  referenceColumn?: PayrollItemReferenceColumn;

  // 以下は項目の「設定項目」としては UI に出さない（詳細カード・編集ダイアログの対象外）。
  // 帳票掲載タブ（payroll-item-report-usage）と給与明細書マスタ設定が掲載ルールの
  // 導出に使うため、データとしては保持する。
  /** 給与明細上の表示名 (`name` が制度上の正式名で、明細では別名で出すケース)。 */
  paySlipDisplayName?: string;
  /** 給与明細上のロール。入力データやマスタ等の非明細項目は undefined。 */
  type?: PayrollItemType;
  /** 制定根拠。`type` 未設定の item では undefined。 */
  provisionBasis?: PayrollItemProvisionBasis;
  /** 月次変動性。`type` 未設定の item では undefined。 */
  variability?: PayrollItemVariability;
  /** 課税対象性。`type === "earning"` の item で意味を持つ。 */
  taxability?: PayrollItemTaxability;
  /** 給与明細上の表示順 (昇順)。明細項目でない item は undefined。 */
  paySlipOrder?: number;
  /** 賃金台帳上の表示順 (昇順)。台帳項目でない item は undefined。 */
  wageLedgerOrder?: number;
}

/** Logic component の引数の多重度。
 *  required: 必須・単一。repeatable: 1..n の可変長で、最低 minCount 件の指定が必要。 */
export type PayrollLogicArgumentArity = 'required' | 'repeatable';

/** 引数に与えられる値の制約。丸め桁・日数のように、多重度だけでは表せない
 *  値そのものの条件を持つ引数のために使う。表示には出さず、引数の入力 UI /
 *  dry run 実装が参照する。 */
export interface PayrollLogicArgumentValueConstraint {
  /** 整数のみ許容する。 */
  integer?: boolean;
  /** 下限（この値を含む）。例: 日割対象日数は 0 以上。 */
  min?: number;
  /** 下限（この値を含まない）。例: 所定日数は 0 より大きい。 */
  exclusiveMin?: number;
  /** 単独の数値条件では表せない補足。他引数との関係や桁指定の意味づけなど。 */
  note?: string;
}

/** Logic component の引数定義。UI の ARGUMENT 列 1 行に対応する。 */
export interface PayrollLogicComponentArgument {
  /** 引数を一意に識別するキー（例: 'addend'）。step view の arg id 生成に使う。 */
  key: string;
  /** 引数名（日本語）。例: 「加えられる数」 */
  name: string;
  /** 引数名（英語）。例: 'Starting Value' */
  nameEn: string;
  /** ARGUMENT 列の引数名の前に出す Lucide アイコン。 */
  iconName: IconName;
  arity: PayrollLogicArgumentArity;
  /** arity が 'repeatable' のときに必要な最小件数。 */
  minCount?: number;
  /** 値そのものの制約。制約を持たない引数では undefined。 */
  valueConstraint?: PayrollLogicArgumentValueConstraint;
}

/** Logic component の返り値定義。RETURN 行に出す。 */
export interface PayrollLogicComponentReturnItem {
  /** 返り値名（日本語）。例: 「和」 */
  name: string;
  /** 返り値名（英語）。例: 'Sum' */
  nameEn: string;
}

/** Payroll engine の logic component 原子マスタ。
 *  「料率テーブル Lookup」「労使折半」「端数処理」「× 12 月割」のような計算ロジック atom。 */
export interface PayrollLogicComponent {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  /** 説明（英語）。`description` と同じ内容を英語で持つ。 */
  descriptionEn: string;
  countryCode: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  config: string;
  validFrom: string;
  validTo: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  displayBadgeKind?: string;
  /** LogicComponentCard の glyph (枠の中の絵柄) を決める。 */
  glyphKind?: LogicComponentGlyphKind;
  /** 引数定義（表示順）。この logic を step に置いたとき ARGUMENT 列に並ぶ行。 */
  arguments: PayrollLogicComponentArgument[];
  /** 返り値定義。RETURN 行に出す。 */
  returnItem: PayrollLogicComponentReturnItem;
}

/** Attendance engine の値型。実データ確定まで Payroll 側の union をそのまま使う。 */
export type AttendanceValueType = PayrollValueType;

/**
 * Attendance item の分類。
 *
 * payroll の 9 値のうち、帳簿の支給・控除まわり（earnings / deductions / adjustments /
 * employerContributions / reimbursements / paymentsInKind）は給与明細の軸であって
 * 勤怠計算項目には該当が無いため持たない。
 *
 * 集計は**粒度**で分ける。勤怠の項目は値の器であって、日次項目は (従業員 × 日) の行を、
 * 月次項目は (従業員 × 月) の行を持つ。粒度が違えば行数も突き合わせ方も違うため、
 * 「集計」の一枠にまとめると 70 件超が混在して一覧として使えない。
 *
 * - `dailyAggregates` … 日ごとに 1 行。労働時間・時間区分・遅刻早退など
 * - `weeklyAggregates` … 週ごとに 1 行。週の法定枠で初めて分かる時間外など
 * - `monthlyAggregates` … 月ごとに 1 行。給与エンジンへ渡す項目と法令判定
 * - `periodAggregates` … フレックスの清算期間ごとに 1 行。総枠と過不足
 * - `references` … 打刻・カレンダーから取り込む入力データ
 * - `inquiry` … 参照テーブル（丸め規則・法定労働時間・36協定の上限など）そのもの
 *
 * 一覧の side panel は `references` の枠が `inquiry` も拾うため 5 枠になる
 * （payroll と同じ扱い。追加フローの「照会先」必須判定と対応表カードの表示切替が
 * 両者の区別に依存するので、category としては統合しない）。
 */
export type AttendanceItemCategory = 'dailyAggregates' | 'weeklyAggregates' | 'monthlyAggregates' | 'periodAggregates' | 'references' | 'inquiry';
export type AttendanceUnitOfMeasurement = PayrollUnitOfMeasurement;
export type AttendanceItemReferenceColumn = PayrollItemReferenceColumn;
export type AttendanceLogicComponentArgument = PayrollLogicComponentArgument;
export type AttendanceLogicComponentReturnItem = PayrollLogicComponentReturnItem;
export type AttendanceItemType = PayrollItemType;
export type AttendanceItemProvisionBasis = PayrollItemProvisionBasis;
export type AttendanceItemVariability = PayrollItemVariability;
export type AttendanceItemTaxability = PayrollItemTaxability;

/** Attendance engine の item 原子マスタ。PayrollItem と同形。 */
export interface AttendanceItem {
  id: string;
  name: string;
  nameEn: string;
  /** 計算式・帳票から参照するコード。一覧のコード列・詳細 Identity カードに表示する。 */
  code: string;
  description: string;
  countryCode: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  config: string;
  validFrom: string;
  validTo: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  displayBadge?: string;
  displayBadgeKind?: string;
  /** 項目が取る値の型。 */
  valueType: AttendanceValueType;
  /** 計量単位。未設定の item は表示上 '—' になる。 */
  unitOfMeasurement?: AttendanceUnitOfMeasurement;
  /** TODO(attendance-data): 実データ確定時に文言を見直す。帳簿上の分類（支給・控除・参照・照会）。 */
  category: AttendanceItemCategory;
  /** 管理タグ。一覧・詳細で badge 表示し、フィルタ対象になる。 */
  managementTags?: string[];
  /** 値を引く表と、その表から選んだ 1 カラム。参照テーブル（R048 / R049）や
   *  照会表（号俸表など）のように `valueType: 'table'` の item を
   *  `tableItemId` で名指しし、`column` にその表の列見出しを持つ。 */
  referenceColumn?: AttendanceItemReferenceColumn;

  // TODO(attendance-data): 実データ確定時に文言を見直す。以下は項目の「設定項目」としては UI に出さない
  // （詳細カード・編集ダイアログの対象外）。帳票掲載タブ（payroll-item-report-usage）と給与明細書
  // マスタ設定が掲載ルールの導出に使うため、データとしては保持する。
  /** TODO(attendance-data): 実データ確定時に文言を見直す。給与明細上の表示名 (`name` が制度上の正式名で、明細では別名で出すケース)。 */
  paySlipDisplayName?: string;
  /** TODO(attendance-data): 実データ確定時に文言を見直す。給与明細上のロール。入力データやマスタ等の非明細項目は undefined。 */
  type?: AttendanceItemType;
  /** 制定根拠。`type` 未設定の item では undefined。 */
  provisionBasis?: AttendanceItemProvisionBasis;
  /** 月次変動性。`type` 未設定の item では undefined。 */
  variability?: AttendanceItemVariability;
  /** TODO(attendance-data): 実データ確定時に文言を見直す。課税対象性。`type === "earning"` の item で意味を持つ。 */
  taxability?: AttendanceItemTaxability;
  /** TODO(attendance-data): 実データ確定時に文言を見直す。給与明細上の表示順 (昇順)。明細項目でない item は undefined。 */
  paySlipOrder?: number;
  /** TODO(attendance-data): 実データ確定時に文言を見直す。賃金台帳上の表示順 (昇順)。台帳項目でない item は undefined。 */
  wageLedgerOrder?: number;
}

/**
 * Attendance engine が使う logic component。
 *
 * 勤怠固有のカタログは持たず、`payrollLogicComponents` を給与と共有する
 * （mock DB v86 で `attendanceLogicComponents` テーブルを廃止）。勤怠の計算単位が
 * 置く演算（加減乗除・最大最小・条件分岐・表引き・丸め）は payroll 側の語彙で
 * 足りるため、同じ演算を二重に持たない。
 */
export type AttendanceLogicComponent = PayrollLogicComponent;
export interface AttendanceStructure {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  /** この structure が参照する Unit の id (順序つき)。Unit はカタログ側で独立に存在する。 */
  unitIds: string[];
  /** この structure が直接参照する AttendanceItem の id */
  itemIds: string[];
  /** この structure が直接参照する AttendanceLogicComponent の id */
  logicComponentIds: string[];
  compositionStages?: string[][];
  /** AttendanceLogicComponent の id (= payment cycle として扱う logic component) */
  paymentCycleId?: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  /** 適用開始日。未来日の場合、一覧の scheduled タブに表示する。 */
  validFrom?: string;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
export interface PayrollStructure {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  /** この structure が参照する Unit の id (順序つき)。Unit はカタログ側で独立に存在する。 */
  unitIds: string[];
  /** この structure が直接参照する PayrollItem の id */
  itemIds: string[];
  /** この structure が直接参照する PayrollLogicComponent の id */
  logicComponentIds: string[];
  compositionStages?: string[][];
  /** PayrollLogicComponent の id (= payment cycle として扱う logic component) */
  paymentCycleId?: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  /** 適用開始日。未来日の場合、一覧の scheduled タブに表示する。 */
  validFrom?: string;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Unit / UnitItem / UnitLogicComponent (catalog atoms, structure 非従属) ─────────

// 型は features/engine-shared/structure-detail/types.ts が所有。mocks 側は consumer。
export type { StructureCellRole, StructureTablePreview } from '@/features/engine-shared/structure-detail/types';

/** PayrollUnit はカタログ atom として独立に存在する。Structure は unitIds で参照する。 */
export interface PayrollUnit {
  id: string;
  name: string;
  description?: string;
  branchCondition?: string;
  branchLabel?: 'T' | 'F';
  order: number;
  /** 所属する Stage ノードの key（各 payroll structure seed の nodes.ts）。未設定の Unit は見出しを出さない。 */
  stageKey?: string;
  lifecycleStatus: EngineLifecycleStatus;
  /** 適用開始日。未来日の場合、一覧の scheduled タブに表示する。 */
  validFrom?: string;
  countryCode: string;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** 計算式・帳票から参照するコード。一覧のコード列に表示する。 */
  code: string;
}

/** PayrollUnit の input / output に登場する item の join 行。
 *  表示用情報は itemId → PayrollItem を join して取得する。 */
export interface PayrollUnitItem {
  id: string;
  unitId: string;
  /** FK → payrollItems.id。branch label 用 unit item (True/False output) は省略可。 */
  itemId?: string;
  role: Exclude<StructureCellRole, 'process'>;
  order: number;
  /** subject/period 依存の mock 表示値 ("472,460" 等)。未指定なら空欄。 */
  valueOverride?: string;
  /** 分岐 unit の True/False output 用。itemId と排他。 */
  branchLabel?: 'T' | 'F';
  /** 分岐先 unit の名前 (例: "介護保険料計算")。 */
  branchTargetUnitName?: string;
}

/** PayrollUnit の process に登場する logic component の join 行。
 *  表示用情報は logicComponentId → PayrollLogicComponent を join して取得する。 */
export interface PayrollUnitLogicComponent {
  id: string;
  unitId: string;
  logicComponentId: string;
  order: number;
  valueOverride?: string;
  /** 算術ロジックの計算に用いる input item ID 群。
   *  例: subtract の場合は被減数と減数の itemId。
   *  expanded 表示時に「何を使った計算か」を可視化するための情報。 */
  inputItemIds?: string[];
}
export interface AttendanceUnit {
  id: string;
  name: string;
  description?: string;
  parentUnitId?: string;
  branchCondition?: string;
  branchLabel?: 'T' | 'F';
  order: number;
  /** サイドタブで stage 見出しを差し込むためのキー。Structure 側で同じ key を持つ substage が連続する区間に見出しを表示する。未設定の Unit は見出しを出さない（後方互換）。 */
  stageKey?: string;
  lifecycleStatus: EngineLifecycleStatus;
  /** 適用開始日。未来日の場合、一覧の scheduled タブに表示する。 */
  validFrom?: string;
  countryCode: string;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** 計算式・帳票から参照するコード。一覧のコード列に表示する。 */
  code: string;
  /** 配下 unit を持つ substage の対象者フィルタ。people-group の customFilter と同形。 */
  filterRule?: StructureSubstageFilterRule | null;
}
export interface AttendanceUnitItem {
  id: string;
  unitId: string;
  itemId?: string;
  role: Exclude<StructureCellRole, 'process'>;
  order: number;
  valueOverride?: string;
  branchLabel?: 'T' | 'F';
  branchTargetUnitName?: string;
}
export interface AttendanceUnitLogicComponent {
  id: string;
  unitId: string;
  logicComponentId: string;
  order: number;
  valueOverride?: string;
  /** 算術ロジックの計算に用いる input item ID 群。
   *  expanded 表示時に「何を使った計算か」を可視化するための情報。 */
  inputItemIds?: string[];
}

// ─── Core Domain ────────────────────────────────────────────

export type PayrollAttendanceMetricType = 'employee_count' | 'payroll_status' | 'attendance_rate' | 'overtime_hours' | 'alert_count' | 'processing_progress';
export type MetricTrend = 'up' | 'down' | 'stable';
export type OperationPackageType = 'payroll' | 'attendance' | 'payroll_attendance';
export type OperationPackageCategory = 'monthly_close' | 'year_end' | 'onboarding' | 'offboarding' | 'mid_year_adjustment' | 'bonus_processing';
export type OperationPackageStatus = 'template' | 'in_progress' | 'completed';
export interface PayrollAttendanceDashboardMetric {
  id: string;
  period: string;
  metricType: PayrollAttendanceMetricType;
  label: string;
  value: number;
  unit: string;
  trend: MetricTrend;
  trendPercent: number;
  organizationId: string;
  updatedAt: string;
}
export interface OperationPackage {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  type: OperationPackageType;
  category: OperationPackageCategory;
  steps: string;
  estimatedDuration: string;
  assigneeId: string | null;
  status: OperationPackageStatus;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
export interface AttendanceOpPackage {
  id: string;
  groupIndex: number;
  sortOrder: number;
  inProgressCount: number;
}
export interface AttendanceOpPackagePattern {
  id: string;
  packageId: string;
  sortOrder: number;
  nameKey: string;
}
export interface AttendanceOpPackageInstanceItem {
  id: string;
  patternId: string;
  sortOrder: number;
  row: SchemaTableRow;
}
export interface PayrollOpPackage {
  id: string;
  groupIndex: number;
  sortOrder: number;
  inProgressCount: number;
}
export interface PayrollOpPackagePattern {
  id: string;
  packageId: string;
  sortOrder: number;
  nameKey: string;
}
export interface PayrollOpPackageInstanceItem {
  id: string;
  patternId: string;
  sortOrder: number;
  row: SchemaTableRow;
}

// ─── Attendance Rule & Policy ───────────────────────────────

export type AttendanceRulePolicyCategory = 'assignment' | 'attendance' | 'period';
export interface AttendanceRulePolicy {
  id: string;
  category: AttendanceRulePolicyCategory;
  name: string;
  description: string;
  mandatory: boolean;
  clauseTypes: string[];
  config: string;
  organizationId: string;
  updatedAt: string;
}

// ─── Payroll Alert ──────────────────────────────────────────

export type PayrollAlertCategory = 'calculation' | 'compliance' | 'payment' | 'deadline';
export type PayrollAlertType = 'calc_failed' | 'negative_net_pay' | 'deduction_exceeds_gross' | 'minimum_wage_violation' | 'overtime_rate_mismatch' | 'withholding_tax_anomaly' | 'missing_bank_account' | 'fb_data_mismatch' | 'payment_date_overdue' | 'closing_deadline_exceeded' | 'resident_tax_update_due' | 'year_end_adjustment_pending';

// ─── Employee Pay Slip Alerts (detail page) ─────────────────
// pay slip 詳細画面の alerts タブ専用。employee 単位で seed する。

export type EmployeePaySlipAlertCategory = PayrollAlertCategory;
export type EmployeePaySlipAlertType = PayrollAlertType;
export interface EmployeePaySlipAlert {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  period: string;
  payGroup: string;
  category: EmployeePaySlipAlertCategory;
  type: EmployeePaySlipAlertType;
  severity: AlertSeverity;
  message: string;
  detectedAt: string;
  resolved: boolean;
  resolvedAt: string | null;
  referenceId: string | null;
  organizationId: string;
}

// ─── Employee Clocking Record Alerts (detail page) ──────────
// attendance alerts と同形だが、clocking record 詳細画面の alerts タブ専用に独立テーブルで保持する。

// 基底 AttendanceAlertType(staff サマリ/給与で利用)に加え、clocking record
// 詳細のアラートタブ専用で追加種別を持つ。基底は変更しない（他機能への影響回避）。
export type EmployeeClockingRecordAlertType = AttendanceAlertType | 'late_arrival' | 'early_leave' | 'holiday_work';
export interface EmployeeClockingRecordAlert {
  id: string;
  employeeId: string;
  employeeName: string;
  type: EmployeeClockingRecordAlertType;
  severity: AlertSeverity;
  message: string;
  date: string;
  resolved: boolean;
  resolvedAt: string | null;
  organizationId: string;
  createdAt: string;
}

// ─── Employee Pay Slip / Clocking Record Files (detail page) ─
// device files の簡易版。folder type 単位でファイル一覧を保持する mock 専用テーブル。

export type EmployeePaySlipFileFolderType = 'payslip' | 'bonus' | 'yearEndAdjustment';
export type EmployeeClockingRecordFileFolderType = 'attendanceBook' | 'shiftTable' | 'leaveRequest';
export interface EmployeePaySlipDetailFile {
  id: string;
  employeeId: string;
  organizationId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  folderType: EmployeePaySlipFileFolderType;
  uploadedByName: string;
  uploadedAt: string;
  updatedAt: string;
}
export interface EmployeeClockingRecordFile {
  id: string;
  employeeId: string;
  organizationId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  folderType: EmployeeClockingRecordFileFolderType;
  uploadedByName: string;
  uploadedAt: string;
  updatedAt: string;
}

// ─── Attendance Operation Activity ──────────────────────────

export type AttendanceActivityCategory = 'clocking' | 'request' | 'alert' | 'policy';
export type AttendanceActivityType = 'clocking.created' | 'clocking.edited' | 'clocking.deleted' | 'clocking.approved' | 'clocking.rejected' | 'request.leaveSubmitted' | 'request.overtimeSubmitted' | 'request.holidayWorkSubmitted' | 'request.approved' | 'request.rejected' | 'request.cancelled' | 'alert.triggered' | 'alert.acknowledged' | 'alert.resolved' | 'policy.ruleUpdated' | 'policy.attendanceStructureAssigned' | 'shift.assigned' | 'attendance.monthlyClosed' | 'attendance.reopened';
export type ActivityActorKind = 'user' | 'system';
export type ActivityOriginKind = 'manual' | 'operation';
export interface AttendanceOperationActivity {
  id: string;
  correlationId: string;
  occurredAt: string;
  category: AttendanceActivityCategory;
  activityType: AttendanceActivityType;
  targetEmployeeId: string;
  targetEmployeeName: string;
  actorId: string | null;
  actorName: string | null;
  actorKind: ActivityActorKind;
  originKind: ActivityOriginKind;
  operationTaskName: string | null;
  operationPackageName: string | null;
  patternName: string | null;
  detail: string;
  payload: string;
  organizationId: string;
}

// ─── Individual Portal Mock (visual-only dashboard) ─────────

/** 打刻種別。休憩は1日に複数回起こりうるため出退勤と同じイベント列で扱う。 */
export type IndividualPortalPunchType = 'clockIn' | 'breakStart' | 'breakEnd' | 'clockOut';

/** 1 回の打刻イベント。記録順（時系列）に積まれる。 */
export interface IndividualPortalPunch {
  type: IndividualPortalPunchType;
  /** ISO datetime recorded at punch time. */
  at: string;
}
export interface IndividualPortalAttendance {
  id: string;
  /** ISO date string for the displayed day (e.g. "2025-03-28"). */
  date: string;
  /** When true, the "打刻漏れ" alert row is shown. */
  hasMissingPunch: boolean;
  /** 打刻イベントの記録列。記録された分だけ時系列で保持する。 */
  punches: IndividualPortalPunch[];
}
export interface IndividualPortalTask {
  id: string;
  name: string;
  /** ISO date string. */
  dueDate: string;
  completedSteps: number;
  totalSteps: number;
  /** Sort order for stable rendering. */
  sortOrder: number;
  /**
   * When set, clicking the task in the portal opens the sequence runner at
   * `/individual/portal/sequence/{sequenceInstanceId}`. Leave undefined for
   * static / non-interactive task rows.
   */
  sequenceInstanceId?: string;
  /**
   * 本人起点の手続きタスク（デモ）用の直接遷移先。設定時は sequenceInstanceId より優先し、
   * クリックで当該手続きフォームを開く。
   */
  to?: '/individual/portal/procedures/offboarding' | '/individual/portal/procedures/onboarding';
}

// ─── Individual Portal Sequence (AI-driven flow) ────────────

export type IndividualPortalSequenceKind = 'year_end_adjustment' | 'onboarding' | 'hire_event' | 'clock_correction' | 'shift_rostering';

/** Speaker identity rendered above a chat bubble when multiple parties are present. */
export interface SequenceChatIdentity {
  name: string;
  avatarUrl?: string;
  avatarFallback?: string;
}

/** A pre-baked chat history entry shown above the floating chat card. */
export interface SequenceChatHistoryItem {
  key: string;
  kind: 'ai_bubble' | 'user_summary_card' | 'user_file_card' | 'draft_link' | 'tool_call' | 'automap_link';
  /** AI bubble text — supports `<strong>` and `<br/>` placeholder tokens. */
  text?: string;
  /** Title for summary cards ("STEP 1 回答"). */
  title?: string;
  /** Rows for summary cards. */
  rows?: {
    label: string;
    value: string;
  }[];
  /** File entries for `user_file_card`. */
  files?: {
    name: string;
  }[];
  /** Optional speaker identity. Falls back to default by `kind` when omitted. */
  identity?: SequenceChatIdentity;
  /** ISO datetime string. Optional footer display below the bubble. */
  timestamp?: string;
  /**
   * Optional direction override. By default user_summary_card / user_file_card は outgoing、
   * ai_bubble は incoming に表示される。
   * 例: 他者から転送された情報を `user_summary_card` で表示するときは "incoming" を指定する。
   */
  direction?: 'incoming' | 'outgoing';
  /** Label for the inline link button on `draft_link` / `automap_link`. */
  linkLabel?: string;
  /**
   * `draft_link` で開く detail content の指定（複数 content を持つシーケンス用）。
   * 例: シフト編成は "shift-requests"（シフト希望）。未指定なら既定 content。
   */
  draftTargetKey?: string;
  /**
   * Tool name shown on `tool_call` chips (例: "Automap Ingester")。
   * 直近の pending tool_call item に対しては、reveal 前の thinking インジケーターが
   * "Calling ${toolName}..." と表示される。
   */
  toolName?: string;
}
export type SequenceAnswerSpec = {
  kind: 'yes_no';
  yesLabel: string;
  noLabel: string;
  /**
   * YES/NO の上に表示する補助情報 callout（例: 病院予約連絡先 / 招待メールの宛先・件名）。任意。
   * iconName は seed が指定する抽象識別子で、renderer 側で IconName に解決する。
   * rows が 1 件のときは prominent な大きい表示、複数件のときは label/value ペアを縦に並べる。
   */
  callout?: {
    iconName: string;
    rows: Array<{
      label: string;
      value: string;
    }>;
  };
} | {
  kind: 'single_choice';
  options: Array<{
    key: string;
    pill?: string;
    label: string;
    sub?: string;
  }>;
} | {
  kind: 'single_choice_with_specs';
  submitLabel: string;
  options: Array<{
    key: string;
    pill?: string;
    label: string;
    sub?: string;
    specs: {
      label: string;
      value: string;
    }[];
  }>;
} | {
  kind: 'multi_choice';
  defaultSelected?: string[];
  submitLabel: string;
  options: Array<{
    key: string;
    label: string;
    sub?: string;
  }>;
} | {
  kind: 'amount';
  label: string;
  placeholder: string;
  suffix: string;
  submitLabel: string;
  quickValues: {
    value: string;
    label: string;
  }[];
  format: 'yen';
} | {
  kind: 'address_form';
  submitLabel: string;
  fields: Array<{
    id: string;
    label?: string;
    placeholder: string;
    defaultValue?: string;
    kind: 'postal' | 'city' | 'street' | 'tel' | 'text' | 'email';
  }>;
} | {
  kind: 'text_input';
  label?: string;
  placeholder: string;
  defaultValue?: string;
  submitLabel: string;
  maxLength?: number;
  /** 任意質問の場合に「回答しない」ボタンを表示するためのラベル。未指定なら skip 不可。 */
  skipLabel?: string;
} | {
  kind: 'date_input';
  label?: string;
  defaultValue?: string;
  submitLabel: string;
  min?: string;
  max?: string;
} | {
  kind: 'field_confirm';
  rows: Array<{
    id: string;
    label: string;
    value: string;
  }>;
  yesLabel: string;
  noLabel: string;
} | {
  kind: 'upload';
  hint: string;
  skipOption?: {
    key: string;
    pill?: string;
    label: string;
    sub?: string;
  };
  manualOption?: {
    key: string;
    pill?: string;
    label: string;
    sub?: string;
  };
  acceptedMimeTypes: string[];
  maxSizeBytes: number;
} | {
  kind: 'automap_review';
  title: string;
  subtitle: string;
  /** Upload step whose previewDataUrl + file names are surfaced on the left pane. */
  uploadStepId: string;
  certificates: AutomapCertificate[];
  confirmLabel: string;
} | {
  kind: 'complete';
  ctaLabel: string;
};
export interface SequenceStepDefinition {
  id: string;
  order: number;
  /** Right-panel + sidebar label, e.g. "個人情報・扶養家族の確認". */
  navLabel: string;
  /** Floating chat card title. */
  title: string;
  /** Floating chat card subtitle. */
  subtitle?: string;
  /** Pre-baked AI history shown above the floating card on entry. */
  history: SequenceChatHistoryItem[];
  answer: SequenceAnswerSpec;
}
export interface SequenceStackingDoc {
  date: string;
  iconName: string;
  label: string;
  /**
   * Reference エントリをクリックしたときに開くパネル。
   * 指定すると label がボタンになり、対応するハンドラが呼ばれる。
   * - "automap": Automap Ingester パネル
   * - "detail": detail draft パネル（シフト編成の希望/週/日/月など）
   */
  action?: 'automap' | 'detail';
  /**
   * detail パネル内で開く対象（複数の detail content を持つシーケンス用）。
   * 例: シフト編成は "store-schedule"（店舗のシフト表）/ "shift-requests"（シフト希望）。
   * 未指定なら detail の既定 content を開く。
   */
  draftTargetKey?: string;
  /**
   * このエントリを表示し始める step id。指定された step に到達するまでは
   * stackingDocs から隠す。未指定なら最初から表示される。
   * 例: "ye-step-automap" を指定すると、抽出 step の chat やりとりが
   *     発生して以降のみレファレンスに現れる。
   */
  visibleAfterStepId?: string;
}
export type DraftBadgeTone = 'primary' | 'success' | 'warning' | 'neutral' | 'draft';
export type DraftDcBlock = {
  kind: 'field_group';
  title: string;
  cols: 1 | 2;
  fields: {
    label: string;
    value: string;
  }[];
} | {
  kind: 'deduction_table';
  title?: string;
  rows: {
    provider: string;
    type: string;
    period?: string;
    amount: string;
  }[];
} | {
  kind: 'section_divider';
  iconName?: string;
  label: string;
  rightBadge?: {
    text: string;
    tone: DraftBadgeTone;
  };
} | {
  kind: 'info_callout';
  iconName: string;
  text: string;
} | {
  kind: 'summary_table';
  title?: string;
  headerLabels: {
    type: string;
    details: string;
    amount: string;
  };
  rows: {
    type: string;
    provider: string;
    amount: string;
  }[];
  totalRowLabel: string;
  totalAmount: string;
} | {
  kind: 'doc_table';
  rows: {
    name: string;
    fileName: string;
    status: {
      label: string;
      tone: DraftBadgeTone;
    };
  }[];
};
export interface DraftDcContent {
  badge: string;
  title: string;
  subtitle: string;
  blocks: DraftDcBlock[];
}

/** 文書系シーケンス（年末調整 / 入社手続き 等）の detail draft。DC ブロック + プレーン版。 */
export interface SequenceDocumentDetailDraft {
  kind: Exclude<IndividualPortalSequenceKind, 'shift_rostering'>;
  draftDc: DraftDcContent;
  draftPlain: string;
}

/**
 * スタッフから提出された 1 名分のシフト希望（mock seed）。
 * detail view の「希望」タブで一覧し、店長が反映の判断に使う。
 */
export interface ShiftRequestDraft {
  staffId: string;
  staffName: string;
  position: ShiftPosition;
  /**
   * 締切後に提出された遅れ組。`requestsCompleteAtStepIndex` 到達まで未提出として扱い、
   * 「全員提出が揃う」演出に使う。
   */
  submittedLate?: boolean;
  /** 希望スロット。`kind` で勤務希望 / 休み希望 / 時間限定を区別する。 */
  entries: Array<{
    /** "YYYY-MM-DD"。 */
    date: string;
    startH: number;
    endH: number;
    kind: 'preferred' | 'off' | 'limited';
    note?: string;
  }>;
}

/**
 * 店長のシフト編成 detail view。希望 / 週 / 日 / 月 を切替表示する。
 * 週/日/月グリッドは Dexie の店舗シフト実データ（getStoreShiftDetail）を読み、
 * 希望タブは seed された `requests` を表示する。
 */
export interface ShiftScheduleDetailDraft {
  kind: 'shift_rostering';
  /** 表示対象の店舗（pharmacyStores）。 */
  storeId: string;
  /** 対象月 "YYYY-MM"（週/日/月グリッドの読み取り範囲）。 */
  monthPrefix: string;
  /** 詳細の初期アンカー日 "YYYY-MM-DD"。 */
  anchorDate: string;
  /** スタッフから提出されたシフト希望。 */
  requests: ShiftRequestDraft[];
  /**
   * シフト希望が「提出済み」として表示され始める step index（0 始まり）。
   * 店長が希望提出を依頼する step を完了し、`currentStepIndex` がこの値以上に
   * なるまでは、希望タブは「依頼前」の空状態を表示する（事前提出に見えないようにする）。
   */
  requestsReleasedAtStepIndex: number;
  /**
   * すべてのシフト希望が出揃う step index（0 始まり）。これ未満の間は `submittedLate`
   * のメンバーを未提出として扱う。この値以上で全員提出が揃い、自動生成が可能になる。
   */
  requestsCompleteAtStepIndex: number;
}
export type SequenceDetailDraft = SequenceDocumentDetailDraft | ShiftScheduleDetailDraft;

/**
 * AI mock sequence のサイド。
 * - "individual": 従業員（被管理者）が起点となるフロー（従来から存在）
 * - "cockpit": 人事労務担当者（管理者）が後続業務を実行するフロー
 *
 * `IndividualPortal*` という型名は歴史的経緯（individual 側から先に実装された）。
 * 現在は両サイドの sequence / chat session を単一の Dexie テーブルで保持している。
 */
export type AiMockSide = 'individual' | 'cockpit';

/** v5 以前のレコードは `side` を持たないため、既定値 "individual" として扱う。 */
export function rowSide(row: {
  side?: AiMockSide;
}): AiMockSide {
  return row.side ?? 'individual';
}
export interface IndividualPortalSequenceDefinition {
  id: string;
  kind: IndividualPortalSequenceKind;
  /** どちらのサイドのフローか。未指定の場合は "individual" として扱う。 */
  side?: AiMockSide;
  packageTitle: string;
  shortName: string;
  estimatedMinutes: number;
  steps: SequenceStepDefinition[];
  stackingDocs: SequenceStackingDoc[];
  detailDraft: SequenceDetailDraft;
  /** 詳細パネル下部の確定ボタンのラベル。未指定なら「申請する」。業務処理系では「確定する」など。 */
  submitLabel?: string;
  /** 「申請する」押下後に staged reveal で流す履歴。完了確認 + 受理メッセージ。 */
  completionHistory?: SequenceChatHistoryItem[];
}
export type IndividualPortalSequenceInstanceStatus = 'not_started' | 'in_progress' | 'completed';
export type IndividualPortalSequenceRightPanelMode = 'cards' | 'detail' | 'automap';
export interface IndividualPortalSequenceInstance {
  id: string;
  definitionId: string;
  taskId: string;
  status: IndividualPortalSequenceInstanceStatus;
  currentStepId: string;
  rightPanelMode: IndividualPortalSequenceRightPanelMode;
  sidebarOpen: boolean;
  startedAt: string;
  updatedAt: string;
  completedAt: string | null;
}
export type SequenceUploadedFile = {
  fileName: string;
  sizeBytes: number;
  mimeType: string;
  receivedAt: string;
  /**
   * Image-only inline preview. Populated by the mock upload API via
   * FileReader.readAsDataURL so the Automap review can render the original
   * source after reload. Absent for PDFs and other non-image uploads.
   */
  previewDataUrl?: string;
  /**
   * Runtime-only blob URL (created via URL.createObjectURL).
   * Used to preview real uploaded files (PDF iframe etc.) in the Automap
   * Ingester review screen. Not persisted across reloads.
   */
  objectUrl?: string;
};
export type AutomapFieldConfidence = 'high' | 'medium' | 'low';
export interface AutomapField {
  id: string;
  label: string;
  defaultValue: string;
  kind: 'text' | 'amount';
  confidence: AutomapFieldConfidence;
}
export interface AutomapCertificate {
  id: string;
  documentLabel: string;
  fields: AutomapField[];
}
export type SequenceStepRecordAnswer = {
  kind: 'yes_no';
  value: 'yes' | 'no';
} | {
  kind: 'single_choice';
  selectedKey: string;
} | {
  kind: 'single_choice_with_specs';
  selectedKey: string;
} | {
  kind: 'multi_choice';
  selectedKeys: string[];
} | {
  kind: 'amount';
  value: string;
} | {
  kind: 'address_form';
  values: Record<string, string>;
} | {
  kind: 'field_confirm';
  value: 'yes' | 'no';
} | {
  kind: 'text_input';
  value: string;
} | {
  kind: 'date_input';
  value: string;
} | {
  kind: 'upload';
  files: SequenceUploadedFile[];
  skipped?: boolean;
} | {
  kind: 'automap_review';
  values: Array<{
    certificateId: string;
    fields: Array<{
      id: string;
      value: string;
    }>;
  }>;
} | {
  kind: 'complete';
};
export interface IndividualPortalSequenceStepRecord {
  /** Composite: `${instanceId}:${stepId}` */
  id: string;
  instanceId: string;
  stepId: string;
  status: 'pending' | 'in_progress' | 'completed';
  completedAt: string | null;
  answer: SequenceStepRecordAnswer | null;
}
export type IndividualPortalApprovalStatus = 'pending' | 'in_progress' | 'completed' | 'rejected';
export type IndividualPortalApprovalCategory = 'labor' | 'it' | 'attendance' | 'ga' | 'contract';
export interface IndividualPortalApprovalFlowStep {
  order: number;
  approverName: string;
  approverAvatarColor: string;
  status: 'completed' | 'current' | 'pending';
  /** Set on the step that the current viewer is responsible for. */
  isMe?: boolean;
}
export interface IndividualPortalApprovalFormField {
  label: string;
  value: string;
}
export interface IndividualPortalApproval {
  id: string;
  taskName: string;
  /** ISO date string. */
  approvalDeadline: string;
  /** ISO date string. */
  submittedAt: string;
  requesterName: string;
  /** Avatar accent color used for the round chip behind the requester name. */
  requesterAvatarColor: string;
  approvalFlowLabel: string;
  status: IndividualPortalApprovalStatus;
  category: IndividualPortalApprovalCategory;
  /** Form key/value summary shown in the drawer body. */
  formSummary: IndividualPortalApprovalFormField[];
  /** Approval-flow steps shown in the drawer body. */
  approvalFlow: IndividualPortalApprovalFlowStep[];
  sortOrder: number;
}
export type IndividualPortalRequestApp = 'zoom' | 'zendesk';

// 'submitted' = 申請直後（本人起点の届出が提出され、労務の確認待ち）
export type IndividualPortalRequestStatus = 'in_progress' | 'rejected' | 'submitted';
export interface IndividualPortalRequest {
  id: string;
  name: string;
  /** IT アプリ申請のときだけ設定。労務系の届出（退職申請等）では未設定。 */
  app?: IndividualPortalRequestApp;
  /** ISO date string. */
  submittedAt: string;
  currentStep: number;
  totalSteps: number;
  status: IndividualPortalRequestStatus;
  sortOrder: number;
}
export type IndividualPortalRequestFormCategory = 'attendance' | 'it' | 'labor' | 'contract';
export interface IndividualPortalRequestForm {
  id: string;
  name: string;
  category: IndividualPortalRequestFormCategory;
  sortOrder: number;
  /**
   * BE の OperationPackageType に対応。値が設定されている手続きは Individual から申請可能で、
   * /v2/self-service/operation-packages の packageType と一致したパッケージへ遷移できる。
   */
  packageType?: string;
  /**
   * 本人起点のモック手続きフォームへ直接遷移させるデモ導線用のルート。
   * packageType 経路ではなく指定ルートへ Link する（デモ用）。
   */
  to?: '/individual/portal/procedures/offboarding' | '/individual/portal/procedures/onboarding';
}
export interface IndividualPortalAiChatSession {
  id: string;
  title: string;
  subtitle: string;
  /** ISO datetime. */
  lastInteractedAt: string;
  /** Linked sequence instance id. `null` for non-clickable items. */
  sequenceInstanceId: string | null;
  sortOrder: number;
  /** どちらのサイドのセッションか。未指定の場合は "individual"。 */
  side?: AiMockSide;
}
export interface IndividualPortalUpcomingInteraction {
  id: string;
  title: string;
  subtitle: string;
  /** ISO date. */
  scheduledAt: string;
  sequenceInstanceId: string | null;
  sortOrder: number;
  /** どちらのサイドの予定か。未指定の場合は "individual"。 */
  side?: AiMockSide;
}

// ─── Global AI Chat Mock ─────────────────────────────────────
// セッション・メッセージは BE（oRPC chatContract）が SSOT のためモック型を持たない。
// モデル選択のみ BE に概念が無い FE 専用機能としてモックする。

// ─── UI Preferences (frontend-only, key-value) ──────────────

export interface UiPreference {
  key: string;
  value: unknown;
}

// ─── Pay slip PDF mock (single common blob for "PDFでみる" button) ─

export interface PaySlipPdfMock {
  id: string;
  blob: Blob;
}

// ─── Pharmacy Shift Domain (mock) ───────────────────────────
//
// 薬局/ドラッグストアのシフト管理デモ用。一覧の行単位は「拠点(店舗)」または
// 「組織ユニット(エリア)」で切り替える。シフト・スタッフ・アラートを Dexie で保持する。

/** シフト一覧の行単位。place=店舗（店長視点）/ orgUnit=エリア（俯瞰視点）。 */
export type ShiftUnitType = 'place' | 'orgUnit';

/** 薬局の職種。無資格販売リスク判定では pharmacist / registeredSeller が有資格。 */
export type ShiftPosition = 'pharmacist' | 'registeredSeller' | 'cashier' | 'cosmetics' | 'stocker';
export type ShiftEmploymentType = 'full_time' | 'part_time';

/** 組織ユニット = エリア（関東 / 関西 …）。複数店舗を束ねる俯瞰単位。 */
export interface PharmacyArea {
  id: string;
  name: string;
  sortOrder: number;
}

/** 拠点 = 店舗（渋谷店 / 新宿店 …）。 */
export interface PharmacyStore {
  id: string;
  name: string;
  code: string;
  areaId: string;
  /** 営業時間（24h 表記）。無資格販売リスク（有資格者不在）の判定窓。 */
  openHour: number;
  closeHour: number;
  /** 月次の人件費予算（円）。人件費予算超過アラートの基準。 */
  monthlyLaborBudget: number;
  /** 各時間帯に最低限必要な人員数（シフト未充足判定の基準）。 */
  requiredHeadcount: number;
  sortOrder: number;
}
export interface ShiftStaff {
  id: string;
  storeId: string;
  name: string;
  initials: string;
  position: ShiftPosition;
  /** 薬剤師 / 登録販売者の資格保有（無資格販売リスク判定に使用）。 */
  qualified: boolean;
  employmentType: ShiftEmploymentType;
  /** 時給（円）。人件費の算出に使用。 */
  hourlyWage: number;
}
export interface ShiftAssignment {
  id: string;
  storeId: string;
  staffId: string;
  /** YYYY-MM-DD */
  date: string;
  startH: number;
  endH: number;
  breakStartH: number | null;
  breakEndH: number | null;
  position: ShiftPosition;
  note: string;
}
export type ShiftAlertType = 'unqualified_sales_risk' | 'understaffed' | 'labor_cost_over' | 'overtime_over';
export type ShiftAlertStatus = 'open' | 'requested' | 'resolved';
export type ShiftAlertSeverity = 'warning' | 'critical';
export interface ShiftAlert {
  id: string;
  storeId: string;
  storeName: string;
  type: ShiftAlertType;
  severity: ShiftAlertSeverity;
  /** YYYY-MM-DD（対象日）。 */
  date: string;
  /** 時間帯（無資格販売リスク / 未充足のみ。それ以外は null）。 */
  startH: number | null;
  endH: number | null;
  /** 対象職種（任意）。 */
  position: ShiftPosition | null;
  /** 表示用メッセージ（mock の固定文言。AttendanceAlert.message と同様にデータとして保持）。 */
  message: string;
  status: ShiftAlertStatus;
  /** 応援要請を起票した場合の coverage row id（縦串連携）。 */
  linkedCoverageRequestId: string | null;
  createdAt: string;
}

// ─── 個人ポータル 照会リソース（拠点 / メンバー資格 / デバイス / アカウント / 備品） ─
// 「権限モデル検討 1st」モックの参考デザインに対応する read-only リソース群。
// 操作者本人 = portalPersons の isMe=true（田中 美咲）。

/** 照会リソースの保有者（本人 + 関係者）。device / equipment / certification / account から参照。 */
export interface PortalPerson {
  id: string;
  name: string;
  /** アバター表示用の頭文字。 */
  initials: string;
  email: string;
  /** 所属（部署）。 */
  org: string;
  /** 操作者本人なら true。 */
  isMe: boolean;
}

/** 拠点。本人が責任者として管掌する拠点は managedByMe=true。 */
export interface PortalPlace {
  id: string;
  name: string;
  address: string;
  /** 責任者（portalPersons.id）。未設定なら null。 */
  managerPersonId: string | null;
  /** 操作者本人が管掌する拠点。 */
  managedByMe: boolean;
  sortOrder: number;
}

/** デバイス / 備品の保有形態。assigned=個人割当 / placed=拠点配置。 */
export type PortalAssetStatus = 'assigned' | 'placed';

/** デバイス。 */
export interface MemberDevice {
  id: string;
  name: string;
  /** 種別（ノートPC / タブレット …）。表示はそのまま使用（mock データ）。 */
  category: string;
  assetTag: string;
  status: PortalAssetStatus;
  /** 割当先（個人）。拠点配置なら null。 */
  holderPersonId: string | null;
  /** 設置拠点（拠点に据え置く機器）。 */
  installPlaceId: string | null;
  /** 保管拠点。 */
  storagePlaceId: string | null;
  serialNumber: string;
  os: string;
  /** YYYY-MM-DD */
  purchasedAt: string;
  sortOrder: number;
}

/** 備品。 */
export interface MemberEquipment {
  id: string;
  name: string;
  category: string;
  assetTag: string;
  status: PortalAssetStatus;
  holderPersonId: string | null;
  placeId: string | null;
  /** YYYY-MM-DD */
  purchasedAt: string;
  note: string;
  sortOrder: number;
}

/** メンバー資格。 */
export interface MemberCertification {
  id: string;
  name: string;
  holderPersonId: string;
  /** 認証番号。 */
  number: string;
  /** 発行団体。 */
  issuer: string;
  /** YYYY-MM-DD。未設定なら ''。 */
  lastUpdated: string;
  /** 有効期限。無期限なら null。 */
  expireAt: string | null;
  /** 更新期限。なければ null。 */
  renewBy: string | null;
  note: string;
  sortOrder: number;
}

/** ソフトウェアアカウント。 */
export interface MemberAccount {
  id: string;
  app: string;
  /** ソフトウェアのカテゴリ（種別内訳の集計軸）。 */
  category: string;
  account: string;
  displayName: string;
  role: string;
  /** YYYY-MM-DD */
  createdAt: string;
  /** YYYY-MM-DD */
  lastSignIn: string;
  mfaEnabled: boolean;
  holderPersonId: string;
  sortOrder: number;
}

// ─── 職歴情報（Work Experience）タブ mock（メンバー詳細） ─────

/**
 * 職歴情報タブのローカライズ済み文字列。
 * デモデータは ja / en の2言語のみ保持する（UI ラベルは共有 i18n の6言語対応）。
 */
export interface WorkExperienceLocalizedText {
  ja: string;
  en: string;
}

/** 職歴のポジション（会社配下の子アイテム）。日付は 'YYYY.MM'、在職中は 'CURRENT'。 */
export interface WorkExperiencePosition {
  id: string;
  title: WorkExperienceLocalizedText;
  employment: WorkExperienceLocalizedText;
  start: string;
  end: string;
  grade: WorkExperienceLocalizedText | null;
  department: WorkExperienceLocalizedText;
  responsibilities: WorkExperienceLocalizedText;
}

/** 職歴の会社（親アイテム）。positions は新しい順。 */
export interface WorkExperienceCompany {
  id: string;
  name: WorkExperienceLocalizedText;
  location: WorkExperienceLocalizedText;
  start: string;
  end: string;
  isCurrent: boolean;
  positions: WorkExperiencePosition[];
}

// ─── Device License（デバイス詳細「ライセンス」タブ用の見せ球データ） ───
// 実 BE にデバイス起点でライセンスを逆引きする API が無いためのデモ用擬似データ。
// 実 API 実装時はこのテーブルと seed を削除し、ルートの -api を差し替える。

export interface DeviceLicense {
  id: string;
  /** 将来: 特定デバイスへの紐付け。現状は null=どのデバイスでも共通表示 */
  deviceId: string | null;
  /** ソフトウェア名（インストール型ソフトウェア） */
  softwareName: string;
  /** 製品バージョン */
  productVersion: string;
  /** ライセンス名（プラン・エディション） */
  licenseName: string;
  sortOrder: number;
}

// ─── License Violation Alert（ライセンス配下の違反集約アラート用の見せ球） ───
// 全ライセンス横断の割当違反アラートを集約表示するデモ用擬似データ。
// 実 BE には横断集約 API が無いため（VIOLATED は USER_BASED 単位のみ）、モック DB で保持する。
// 実 API 実装時はこのテーブルと seed を削除し、ルートの -query を差し替える。

export type LicenseViolationCategory = 'NO_LICENSE' | 'VIOLATED' | 'ALUMNI';
export interface LicenseViolationAlert {
  id: string;
  /** 要付与=NO_LICENSE / 要剥奪=VIOLATED / 離籍者=ALUMNI */
  category: LicenseViolationCategory;
  /** 対象ソフトウェア名 */
  softwareName: string;
  /** ライセンス名 */
  licenseName: string;
  /** 対象メンバー（利用者名。該当なしは空文字） */
  targetMember: string;
  /** 対象デバイス（デバイス名。該当なしは空文字） */
  targetDevice: string;
  sortOrder: number;
}

// ─── License 配下タブの見せ球（Overview / メンバー / オペレーションアクティビティ / ルール&ポリシー） ───
// 実 BE の対応 API が未整備のためモック DB で保持する。
// 実 API 実装時はこれらのテーブルと seed を削除し、各ルートの -api を差し替える。

/** メンバータブ：ライセンス付与メンバー */
export interface LicenseMember {
  id: string;
  /** メンバー名 */
  name: string;
  /** 部署 */
  department: string;
  /** 付与されているライセンス */
  licenses: string[];
  sortOrder: number;
}

/** オペレーションアクティビティタブ：ライセンス操作履歴 */
export interface LicenseActivity {
  id: string;
  /** 発生日時 */
  occurredAt: string;
  /** 操作種別 */
  activityTypeLabel: string;
  /** ソフトウェア名 */
  softwareName: string;
  /** ライセンス名 */
  licenseName: string;
  /** 実行者 */
  actorName: string;
  sortOrder: number;
}

/** ルール&ポリシータブ：ライセンス割当ルール（メンバー割当 or デバイス割当の排他） */
export interface LicenseAssignmentRule {
  id: string;
  /** ソフトウェア名 */
  softwareName: string;
  /** ライセンス名 */
  licenseName: string;
  /** 対象メンバーグループ（対象なしは空文字） */
  memberGroup: string;
  /** 対象デバイスグループ（対象なしは空文字） */
  deviceGroup: string;
  sortOrder: number;
}

/** Overview：ソフトウェア別のシート保有/利用状況 */
export interface LicenseSeat {
  id: string;
  /** ソフトウェア名 */
  software: string;
  /** 保有シート数 */
  total: number;
  /** 利用中シート数 */
  assigned: number;
  /** 保有シート数の前月比 */
  totalChange: number;
  /** 利用中シート数の前月比 */
  assignedChange: number;
  sortOrder: number;
}

/** Overview：ライセンス更新予定 */
export interface LicenseRenewal {
  id: string;
  /** ソフトウェア名 */
  softwareName: string;
  /** ライセンス名 */
  licenseName: string;
  /** 更新日（YYYY-MM-DD） */
  renewalDate: string;
  /** シート数 */
  seats: number;
  sortOrder: number;
}

// ─── Re-export gantt snapshot row from operationEventPayload ─

export type { OperationEventSnapshotRow };

// ─── Salary & Wages Domain (給与台帳カタログ E1〜E4c) ─────────────

/** モック対象の12ヶ国コード（台帳カタログの Country 適用マトリクス軸） */
export type SalaryCountryCode = 'JP' | 'US' | 'UK' | 'DE' | 'FR' | 'AU' | 'CN' | 'SG' | 'IN' | 'ID' | 'PH' | 'KR';

/** SW-E1-011 run_type。賞与・年調・退職精算等を単一台帳に統合する区分 */
export type SalaryRunType = 'regular' // 定期給与
| 'bonus' // 賞与
| 'off_cycle' // 臨時・Off-cycle
| 'retro' // 遡及差額
| 'year_end_adj' // 年末調整
| 'termination'; // 退職精算

/** 賃金台帳の法定記載項目。国によって収集可否が異なるため union に 'other' を持つ */
export type SalaryGender = 'male' | 'female' | 'other';

/** SW-E1-050 status */
export type SalaryRecordStatus = 'draft' | 'calculating' | 'calculated' | 'approving' | 'finalized'
// 明細書を本人へ交付済み（法定の明細交付。支払の完了とは別に立つ）
| 'delivered' | 'paid' | 'cancelled';

/** SW-E2-003 line_type */
export type SalaryLineType = 'earning' | 'deduction' | 'employer_contribution' | 'accrual' | 'memo';

/** SW-E4A-001 tax_calc_method。設計原則7の最上位スイッチ */
export type TaxCalcMethod = 'periodic' | 'cumulative';

/** SW-E1-017 pay_frequency */
export type SalaryPayFrequency = 'monthly' | 'semi_monthly' | 'bi_weekly' | 'weekly';

/** SW-E1-034 pay_basis */
export type SalaryPayBasis = 'monthly' | 'daily_monthly' | 'daily' | 'hourly' | 'annual';

/** SW-E2-014 taxability_matrix の1要素。管轄×課税区分の多次元マトリクス。
 *  単一の課税フラグでは US の 401(k)（FIT非課税・FICA課税）を表現できないため行で持つ。 */
export interface TaxabilityEntry {
  /** E4b の tax_code と対応（'FIT' / 'FICA-SS' / 'PAYE' / 'JP-WHT' 等） */
  taxCode: string;
  treatment: 'taxable' | 'non_taxable' | 'partial';
}

/** SW-E2-017 scheme_mapping の1要素。E4c の scheme_code 別の算入区分 */
export interface SchemeMappingEntry {
  /** E4c の scheme_code と対応 */
  schemeCode: string;
  included: boolean;
  /** SG の OW/AW 等、国別の賃金区分 */
  wageClass?: string | null;
}

/** SW-E2-013 / SW-E4A-016 の計算根拠スナップショット（監査再現用） */
export interface SalaryCalcBasisRow {
  label: string;
  value: string;
  unit?: string | null;
}

/** E1: SalaryRecord / 給与明細ヘッダ */
export interface SalaryRecord {
  id: string;
  recordNumber: string;
  tenantId: string;
  companyId: string;
  payrollRunId: string | null;
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  employeeNameSort: string;
  email: string;
  avatarUrl: string | null;
  /** 賃金台帳（労基法107条）の必須記載項目。収集していない国のレコードは null */
  gender: SalaryGender | null;
  /** 同上。ISO 日付 */
  birthDate: string | null;
  countryCode: SalaryCountryCode;
  payGroupId: string;
  payGroupName: string;
  runType: SalaryRunType;
  payPeriodStart: string;
  payPeriodEnd: string;
  paymentDate: string;
  cutoffDate: string | null;
  payFrequency: SalaryPayFrequency;
  fiscalYear: string;
  taxYear: string;
  deptCode: string;
  deptName: string;
  position: string | null;
  grade: string | null;
  employmentType: string;
  payBasis: SalaryPayBasis;
  hireDate: string;
  terminationDate: string | null;
  /** SW-E1-038 管理監督者/Exempt 区分 */
  exemptStatus: 'exempt' | 'non_exempt';
  /** 事業場。賃金台帳は事業場ごとに労働者各人別で調製する（労基法108条）ため冊のキーになる */
  workLocationId: string;
  workLocation: string;
  status: SalaryRecordStatus;
  finalizedAt: string | null;
  finalizedBy: string | null;
  /** SW-E1-054 確定後の変更禁止制御 */
  lockFlag: boolean;
  revisionNumber: number;
  grossAmount: number;
  taxableGross: number;
  nontaxableGross: number;
  overtimeBaseAmount: number;
  totalDeductions: number;
  statutoryDeductions: number;
  agreedDeductions: number;
  netAmount: number;
  employerContributionTotal: number;
  accrualTotal: number;
  totalLaborCost: number;
  paymentCurrency: string;
  baseCurrency: string;

  /** SW-E1-091 再現性担保。Logic Component のバージョン束 */
  calcLogicVersion: string;
  /** SW-E1-092 適用料率セットの凍結参照 */
  rateMasterVersion: string;
  source: 'engine' | 'manual' | 'import';
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

/** E2V の国別適用度。● 必須/主要適用 / ○ 任意・条件付適用 / − 非適用 */
export type CountryApplicability = 'required' | 'optional' | 'not_applicable';

/** E2V: 賃金項目 標準語彙 */
export interface WageItemVocabularyEntry {
  /** 語彙コード（'E-BASE-M' / 'D-WHT' / 'X-SOC-ER' 等）。主キー */
  code: string;
  lineType: SalaryLineType;
  /** カテゴリ（'基準内賃金' / '割増賃金' / '法定控除' 等） */
  category: string;
  nameJa: string;
  nameEn: string;
  /** 課税区分デフォルト（'課税' / '非課税' / '一部非課税' / '−'） */
  taxDefault: string;
  /** 割増基礎デフォルト（'算入' / '除外' / '除外可' / '−'） */
  overtimeBaseDefault: string;
  /** 法的根拠・国別規制。'−' は根拠なし */
  legalBasis: string;
  /** 12ヶ国の適用マトリクス */
  applicability: Record<SalaryCountryCode, CountryApplicability>;
}

/** E2: SalaryLine / 給与明細行 */
export interface SalaryLine {
  id: string;
  recordId: string;
  lineType: SalaryLineType;
  /** E2V の code、またはテナント拡張コード */
  itemCode: string;
  /** 表示名。語彙未定義コードのフォールバック元 */
  itemName: string;
  itemCategory: string;
  displayOrder: number;
  /** 不就労控除は earning 行のマイナス金額として保持する */
  amount: number;
  rate: number | null;
  quantity: number | null;
  calcMethod: 'fixed' | 'variable' | 'formula' | 'manual';
  logicComponentRef: string | null;
  calcBasisSnapshot: SalaryCalcBasisRow[] | null;
  taxabilityMatrix: TaxabilityEntry[];
  nontaxLimit: number | null;
  nontaxExcess: number | null;
  schemeMapping: SchemeMappingEntry[];
  /** SW-E2-018 割増基礎算入区分。JP と US で算入方向が逆転するためフラグ化しない */
  overtimeBaseInclusion: 'included' | 'excluded' | 'country_rule';
  /** SW-E2-023 US の pre-tax → 課税 → post-tax 順序。未使用国は null */
  deductionSequence: {
    phase: 'pre_tax' | 'post_tax';
    priority: number;
  } | null;
  memo: string | null;
}

/** E3: AttendanceSnapshot / 勤怠サマリ（AE締めスナップショット） */
export interface AttendanceSnapshot {
  id: string;
  recordId: string;
  scheduledWorkdays: number;
  daysWorked: number;
  totalWorkdays: number;
  absenceDays: number;
  paidLeaveDays: number;
  legalHolidayWorkdays: number;
  restDayWorkdays: number;
  unpaidLeaveDays: number;
  totalWorkHours: number;
  scheduledWorkHours: number;
  statutoryOtWithin: number | null;
  statutoryOtOver: number;
  /** SW-E3-020 US FLSA の Workweek 単位判定。他国は null */
  weeklyOtBreakdown: {
    weekStart: string;
    workHours: number;
    otHours: number;
  }[] | null;
  nightHours: number;
  legalHolidayHours: number;
  restDayHours: number;
  otOver60h: number | null;
  lateHours: number;
  earlyLeaveHours: number;
  /** 所定労働時間に対する不足時間。不足時間控除の算定根拠 */
  shortHours: number;
  workingTimeScheme: string;
  /** SW-E3-038 上流トレーサビリティ */
  aeBatchRef: string;
  attendanceFinalizedAt: string;
  attendanceAmendedFlag: boolean;
  /** SW-E3-043 割増単価の分母 */
  hourlyBasisHours: number;
}

/** E5 の支給方法。振込先を持たない現金支給を区別する */
export type SalaryPaymentMethod = 'bank_transfer' | 'cash';

/** E5 の充当ルール。定額を先に充て、残りを残額指定の振込先へ回す */
export type SalaryPaymentAllocationRule = 'fixed_amount' | 'remainder';

/**
 * E5: PaymentAllocation / 差引支給額の振り分け（1:N）。
 * 複数口座への振り分けは行の増減で表現し、口座数をカラムに焼き込まない（設計原則6）。
 * 合計は必ず E1 の `netAmount` と一致する。
 */
export interface SalaryPaymentAllocation {
  id: string;
  recordId: string;
  /** 明細のコード列に出す表示コード（'P010' 等） */
  allocationCode: string;
  method: SalaryPaymentMethod;
  allocationRule: SalaryPaymentAllocationRule;
  /** 充当の優先順位。小さいほど先に充当する */
  priority: number;
  amount: number;
  /** 金融機関名。現金支給は null */
  bankName: string | null;
  branchName: string | null;
  accountType: 'savings' | 'checking' | null;
  /** マスク済み値のみ。生の口座番号はこの台帳に一切保持しない */
  accountNumberMasked: string | null;
}

/** E4a: TaxCalculationBasis / 税計算根拠（国横断コア） */
export interface TaxCalculationBasis {
  id: string;
  recordId: string;
  /** 設計原則7の最上位スイッチ */
  taxCalcMethod: TaxCalcMethod;
  /** 'MyNumber' / 'SSN' / 'NINO' 等 */
  taxIdType: string;
  /** Secure Vault 参照。生値は保持しない。表示用マスク値のみ */
  taxIdMasked: string;
  taxResidency: 'resident' | 'non_resident';
  treatyFlag: boolean;
  /** '扶養控除等申告書' / 'W-4' / 'Starter Checklist' 等 */
  declarationDocType: string | null;
  declarationDocStatus: 'submitted' | 'not_submitted' | 'pending_update' | null;
  grossupFlag: boolean;
  /** SW-E4A-015 スキーマレス拡張。コア列に国制度を焼き込まない */
  countryExt: {
    label: string;
    value: string;
  }[];
  calcSnapshot: SalaryCalcBasisRow[];
}

/** E4b: JurisdictionTaxLine / 管轄別税額行（1:N）。多層課税を行の増減で表現する */
export interface JurisdictionTaxLine {
  id: string;
  recordId: string;
  jurisdictionType: 'national' | 'state' | 'province' | 'city' | 'church';
  jurisdictionCode: string;
  jurisdictionName: string;
  /** 'FIT' / 'SIT' / 'PAYE' / 'JP-WHT' 等 */
  taxCode: string;
  taxName: string;
  taxableBase: number;
  rateOrTableRef: string;
  taxAmount: number;
  /** cumulative 国（UK/CN）でのみ非 null */
  ytdTaxableBase: number | null;
  ytdTaxAmount: number | null;
  exemptionStatus: string | null;
  displayOrder: number;
}

/** E4c: SchemeContribution / 社会保障制度別拠出行（1:N）。
 *  「scheme × 基数」概念で全国の社会保険を統一する */
export interface SchemeContribution {
  id: string;
  recordId: string;
  /** 'JP-HI' / 'US-FICA-SS' / 'UK-NI' 等。CE Country Plugin とコード体系共有 */
  schemeCode: string;
  schemeName: string;
  schemeCategory: 'medical' | 'pension' | 'unemployment' | 'accident' | 'ltc' | 'housing' | 'retirement' | 'ethnic_fund' | 'surcharge';
  contributionBase: number;
  baseCapStatus: 'within_range' | 'upper_capped' | 'lower_applied';
  gradeSnapshot: string | null;
  /** SG の OW/AW 二本立て等。該当しない国は null */
  wageClassSplit: {
    wageClass: string;
    amount: number;
  }[] | null;
  employeeRate: number;
  employeeAmount: number;
  employerRate: number;
  employerAmount: number;
  exemptionStatus: 'opted_out' | null;
  eligibilityStatus: 'auto_enrolled' | 'enrolled' | 'opted_out';
  displayOrder: number;
}

// ─── 事業場マスタ（設定 > 組織 > 事業場）─────────────────────

/** 法域。現状は日本のみ */
export type EstablishmentJurisdiction = 'JP';

/** 安衛法の選任義務判定に使う業種区分 */
export type EstablishmentIndustryClass = 'groupA' | 'groupB' | 'groupC';

/** 有効期間。end が null なら現在も有効 */
export interface EstablishmentPeriod {
  /** YYYY-MM-DD */
  start: string;
  /** YYYY-MM-DD。null は無期限 */
  end: string | null;
}

/**
 * 事業場をどう構成するかの宣言。
 * 拠点と組織のどちらか一方ではなく、両方を複数選べる（和集合で帰属を解く）。
 */
export interface EstablishmentSource {
  /** 参照する拠点。2 件以上は統合の例外にあたるため範囲の根拠が必須になる */
  placeIds: string[];
  /** 参照する組織 */
  orgUnitIds: string[];
  /** 選択した組織の配下組織も含めるか */
  includeDescendants: boolean;
  /** 都道府県 / 管轄労基署 / 最低賃金の導出元となる拠点 */
  locationPlaceId: string;
}

/** 事業場。労基法・安衛法の適用単位 */
export interface Establishment {
  id: string;
  jurisdiction: EstablishmentJurisdiction;
  /** 社内コード。一意 */
  code: string;
  name: string;
  source: EstablishmentSource;
  /** 評価順序。小さいほど先に評価される */
  priority: number;
  period: EstablishmentPeriod;
  industryClass: EstablishmentIndustryClass;
  /** 管轄労働基準監督署 */
  laborStandardsOfficeCode: string | null;
  laborInsuranceNumber: string | null;
  socialInsuranceOfficeCode: string | null;
  /** 統合・分割の例外を適用した根拠メモ（監督署への説明用） */
  scopeRationale: string | null;
  updatedAt: string;
}

/** 個人単位の帰属上書きの理由 */
export type EstablishmentOverrideReason = 'secondment' | 'concurrent' | 'telework' | 'other';

/** 個人単位の帰属上書き。出向者・兼務者などの逃がし先 */
export interface EstablishmentMembershipOverride {
  id: string;
  employeeId: string;
  establishmentId: string;
  period: EstablishmentPeriod;
  reason: EstablishmentOverrideReason;
  note: string | null;
  updatedAt: string;
}

/** 拠点マスタ（読み取り専用）。物理的な場所 */
export interface EstablishmentPlace {
  id: string;
  name: string;
  /** JIS 都道府県コード（2 桁） */
  prefectureCode: string;
  prefectureName: string;
  laborStandardsOfficeCode: string;
  sortOrder: number;
}

/** 組織マスタ（読み取り専用） */
export interface EstablishmentOrgUnit {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
}

/** 従業員マスタ（読み取り専用）。事業場の帰属解決に必要な項目だけを持つ */
export interface EstablishmentEmployee {
  id: string;
  employeeNumber: string;
  name: string;
  /** 基準日以降の所属組織 */
  orgUnitId: string;
  /** 勤務地。完全在宅勤務者は null */
  primaryWorkPlaceId: string | null;
  /** 週所定労働時間。常時使用のカウント条件に使う */
  weeklyScheduledHours: number;
  /** 出向中かどうか */
  isSeconded: boolean;
  /** 期中異動日（YYYY-MM-DD）。null なら異動なし */
  orgUnitChangedAt: string | null;
  /** 期中異動前の所属組織 */
  previousOrgUnitId: string | null;
  sortOrder: number;
}

// ─── Salary & Wages ルール&ポリシー: 給与計算体系アサインメントルール ──────

export type { PeopleGroupMembership, PeopleGroupWorker };

/** アサインメントルールが指定する組織ユニット 1 件 */
export interface SalaryWageStructureAssignmentOrgUnit {
  /** {@link EstablishmentOrgUnit} の id */
  orgUnitId: string;
  /** true = 対象に含める / false = 対象から除外する */
  include: boolean;
  /** 配下組織も対象に含めるか */
  allSubordinates: boolean;
}

/**
 * 給与計算体系（{@link PayrollStructure}）へのアサインメントルール。
 *
 * Ordered + Exclusive: `sortOrder` の昇順に評価し、最初に条件が一致した
 * ルールの給与計算体系だけがメンバーに適用される。1 給与計算体系 = 1 ルールで、
 * ルール未作成の給与計算体系は「未設定」として扱う。
 *
 * 条件節（clause）は software 詳細の Rule & Policy と同じ 3 つで、
 * `features/rule-and-policy` の clause UI にそのまま渡せる形で保持する。
 */
export interface SalaryWageStructureAssignmentRule {
  id: string;
  /** 適用先の {@link PayrollStructure} の id */
  payrollStructureId: string;
  /** 評価順（昇順）。小さいほど優先 */
  sortOrder: number;
  description: string;
  /**
   * メンバー種別 clause。種別ごとに ALL / NO_ONE / CUSTOM を持つ。
   * people-group API と同じ形にして `WorkerTypeRuleCard` / `SetMembership` へ渡す。
   */
  memberships: PeopleGroupMembership[];
  /** 組織指定 clause。空配列 = 組織未指定（該当者なし） */
  orgUnits: SalaryWageStructureAssignmentOrgUnit[];
  /**
   * 組織指定 clause に AND で効く適用メンバー種別。API enum 値で保持する。
   * 空配列 = 未選択で、組織指定があっても該当者なしとして扱う。
   */
  workerTypes: WorkerType[];
  /** 個別指定 clause: 条件に関わらず対象に含めるメンバー */
  individuallyIncluded: PeopleGroupWorker[];
  /** 個別指定 clause: 条件に一致しても対象から外すメンバー */
  individuallyExcluded: PeopleGroupWorker[];
  organizationId: string;
  updatedAt: string;
}

// ─── Salary & Wages Alerts ──────────────────────────────────
// 月次計算中のエラーではなく、確定済みの給与明細・賃金台帳・勤怠・人事発令・将来予定から
// 「今後必要になる対応」「将来の給与リスク」を事前に知らせるアラート。

/** アラートの種類（Error 3 種 / Warning 5 種） */
export type SalaryWageAlertItem = 'minimum_wage_shortfall' | 'negative_net_pay' | 'retroactive_calculation' | 'social_insurance_revision' | 'net_pay_drop' | 'payroll_budget_overrun' | 'year_end_adjustment_delta' | 'payroll_item_expiry';

/**
 * error: 将来日を含む確定情報から法令違反・未払い・支払不能・届出漏れが発生すると
 *   合理的に判断できる状態。未解消のまま対象月を確定・支払することは推奨しない。
 * warning: 過去の傾向または未確定の将来情報から、対応が必要になる可能性がある状態。
 */
export type SalaryWageAlertSeverity = 'error' | 'warning';

/** 予測値の扱い。推定値は信頼度と前提を併記し、確定値のように表示しない */
export type SalaryWageAlertValueKind = 'confirmed' | 'scheduled' | 'estimated';

/** 従業員単位のアラートと、部門・法人単位のアラート（資金/予算）を区別する */
export type SalaryWageAlertScope = 'employee' | 'organization';
export interface SalaryWageAlertLabeledValue {
  label: string;
  value: string;
}

/** 「表示すべき内容」の 1 項目。値の確度が問題になる項目だけ `kind` を持つ */
export interface SalaryWageAlertMetric extends SalaryWageAlertLabeledValue {
  kind: SalaryWageAlertValueKind | null;
}
export interface SalaryWageAlertSubject {
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  email: string;
}

/**
 * 識別単位は `対象 × Alert Item × 影響期間` で 1 件。同じ原因が複数月へ影響する場合も
 * 月ごとに増やさず、1 件の中で影響期間として表現する。
 *
 * 判定の説明可能性のため、判定に使った期間・データ・式・閾値・ルールバージョンと、
 * 現在値 / 基準値 / 予測値、直接の理由、推奨対応、次回再判定日時を 1 行で保持する。
 */
export interface SalaryWageAlert {
  id: string;
  item: SalaryWageAlertItem;
  severity: SalaryWageAlertSeverity;
  scope: SalaryWageAlertScope;
  /** 部門・法人単位のアラートは従業員を持たない */
  subject: SalaryWageAlertSubject | null;
  /** 対象組織（従業員単位なら所属部門、法人単位なら「全社」等） */
  organizationName: string;
  /** 勤務地・最低賃金適用地域などの事業場 */
  establishmentName: string;
  /** 適用されている給与体系（{@link PayrollStructure} の name）。組織単位は全体を表す名称 */
  payrollStructureName: string;
  /** 一覧・詳細の見出し（対象と事象が 1 行で分かる要約） */
  headline: string;
  /** 知らせたいこと */
  purpose: string;
  /** アラートになった直接の理由 */
  directReason: string;
  /** 影響期間（同一原因が及ぶ範囲） */
  affectedPeriodStart: string;
  affectedPeriodEnd: string;
  /** 期限（最低賃金の適用開始日 / 次回給与日 / 届出期限 / 項目の終了日 等） */
  dueDate: string;
  /** 期限の意味づけ。「適用開始日」「届出期限」等 */
  dueDateLabel: string;
  detectedAt: string;
  /** 次回の再判定日時 */
  nextEvaluationAt: string;
  currentValue: SalaryWageAlertLabeledValue;
  baselineValue: SalaryWageAlertLabeledValue;
  forecastValue: SalaryWageAlertLabeledValue;
  /** 予測値の種別。confirmed 以外は信頼度と前提を併記する */
  valueKind: SalaryWageAlertValueKind;
  /** 0–1。確定値のみで判定した場合は null */
  forecastConfidence: number | null;
  /** 予測の前提 */
  assumptions: string[];
  /** 表示すべき内容（Alert Item ごとに異なる） */
  metrics: SalaryWageAlertMetric[];
  /** 判定基準 */
  criteria: string[];
  /** 判定式。式で表せない Alert Item は null */
  formula: string | null;
  /** 判定に使った閾値 */
  thresholds: SalaryWageAlertLabeledValue[];
  /** 判定式・閾値・ルールのバージョン */
  ruleVersion: string;
  /** 判定に使用した給与期間 */
  judgedPeriods: string[];
  /** 判定に必要なデータ */
  requiredData: string[];
  /** 発報タイミング */
  triggerTimings: string[];
  /** 推奨する確認・対応 */
  recommendedActions: string[];
  /** Error へ昇格する条件（Error 側は解消条件を書く） */
  escalationNote: string;
  resolved: boolean;
  resolvedAt: string | null;
  organizationId: string;
}

// ─── 給与明細書マスタ（設定 > 給与明細書） ─────────────────────

/** 給与明細書マスタの有効期間 */
export interface PaySlipLayoutPeriod {
  /** YYYY-MM-DD */
  start: string;
  /** YYYY-MM-DD。null は無期限 */
  end: string | null;
}

/**
 * 明細行の区分。給与明細詳細パネル（給与・賃金 > 帳票）の区分見出しと同じ並びで持つ。
 * 事業主負担・積立は従業員に支払われる額ではないため明細書に載せず、ここにも持たない。
 */
export type PaySlipLayoutBodySection = 'attendance' | 'earning' | 'deduction' | 'total';

/**
 * 宛先ブロックの項目が指す固定フィールド。
 * 「誰の・いつの分か」は payroll item ではなく明細書そのものが持つ情報なので、
 * payroll item への紐づけとは別の軸で持つ。
 */
export type PaySlipLayoutHeaderField = 'employeeName' | 'employeeCode' | 'department' | 'calculationPeriod' | 'paymentDate' | 'establishment';

/**
 * 給与明細書に出す 1 項目。定義された項目はすべて印字される（出さない項目は定義から外す）。
 * 制度上の正式名（payroll item の name）と明細書の印字名は別物なので、項目名は独立に持つ。
 */
export interface PaySlipLayoutItem {
  id: string;
  /** 明細書に印字する項目名 */
  label: string;
  /** 紐づける payroll item の id。null は payroll item を持たない項目 */
  payrollItemId: string | null;
  /** 宛先ブロックの固定フィールドを指す項目のみ設定される */
  headerField?: PaySlipLayoutHeaderField;
}

/** 明細行の区分ごとのグループ */
export interface PaySlipLayoutBodyGroup {
  section: PaySlipLayoutBodySection;
  items: PaySlipLayoutItem[];
}

/**
 * 給与明細書のマスタ定義。1 通の明細書の見た目（どの項目をどの名前で出すか）を決める。
 * 事業場と違い評価順を持たない（給与計算体系から一意に引くため、先勝ちの解決が要らない）。
 */
export interface PaySlipLayout {
  id: string;
  name: string;
  /** この明細書を使う給与計算体系。1 通のレイアウトを複数体系で共有できる */
  payrollStructureIds: string[];
  period: PaySlipLayoutPeriod;
  /** 明細行の外に出す項目（宛先・計算期間・支給日など） */
  header: PaySlipLayoutItem[];
  body: PaySlipLayoutBodyGroup[];
  updatedAt: string;
}

/**
 * 給与計算体系マスタ（読み取り専用）。
 * payroll engine の `payrollStructures` は mock に 1 件しか無く「複数体系に紐づく」様子を
 * 出せないため、事業場マスタが拠点・組織を自前で持つのと同じ形で参照用マスタを別に持つ。
 */
export interface PaySlipLayoutStructure {
  id: string;
  name: string;
  sortOrder: number;
}

// ─── 賞与明細書マスタ（設定 > 賞与明細書） ─────────────────────

/**
 * 宛先ブロックの項目が指す固定フィールド（賞与明細書）。
 * 法定表示として賞与支給日を持つ。計算期間は給与明細書と異なり持たない。
 */
export type BonusPaySlipLayoutHeaderField = 'employeeName' | 'employeeCode' | 'department' | 'bonusPaymentDate' | 'establishment';

/** 賞与明細書に出す 1 項目 */
export interface BonusPaySlipLayoutItem {
  id: string;
  label: string;
  payrollItemId: string | null;
  headerField?: BonusPaySlipLayoutHeaderField;
}

/** 明細行の区分ごとのグループ */
export interface BonusPaySlipLayoutBodyGroup {
  section: PaySlipLayoutBodySection;
  items: BonusPaySlipLayoutItem[];
}

/**
 * 賞与明細書のマスタ定義。1 通の明細書の見た目（どの項目をどの名前で出すか）を決める。
 */
export interface BonusPaySlipLayout {
  id: string;
  name: string;
  /** この明細書を使う賞与計算体系 */
  payrollStructureIds: string[];
  period: PaySlipLayoutPeriod;
  header: BonusPaySlipLayoutItem[];
  body: BonusPaySlipLayoutBodyGroup[];
  updatedAt: string;
}

/** 賞与計算体系マスタ（読み取り専用）の runType */
export type BonusPaySlipStructureRunType = 'bonus';

/**
 * 賞与計算体系マスタ（読み取り専用）。
 * 給与明細書用マスタと同様、参照用に別テーブルで持つ。
 */
export interface BonusPaySlipLayoutStructure {
  id: string;
  name: string;
  sortOrder: number;
  runType: BonusPaySlipStructureRunType;
}

// ─── 賃金台帳マスタ（設定 > 賃金台帳） ─────────────────────

/** 帳票出力プロファイル。労基法様式第20号に沿った列構成など */
export type WageLedgerExportProfile = 'form20' | 'standard';

/**
 * 宛先ブロックの項目が指す固定フィールド（賃金台帳）。
 * 氏名・性別・賃金計算期間は HR / 台帳そのものが持つ情報。
 */
export type WageLedgerLayoutHeaderField = 'employeeName' | 'gender' | 'wageCalculationPeriod' | 'establishment' | 'employeeCode';

/** 賃金台帳に出す 1 項目 */
export interface WageLedgerLayoutItem {
  id: string;
  label: string;
  payrollItemId: string | null;
  headerField?: WageLedgerLayoutHeaderField;
}

/** 明細行の区分ごとのグループ */
export interface WageLedgerLayoutBodyGroup {
  section: PaySlipLayoutBodySection;
  items: WageLedgerLayoutItem[];
}

/**
 * 賃金台帳のマスタ定義。1 冊の台帳の見た目（どの項目をどの名前で出すか）を決める。
 */
export interface WageLedgerLayout {
  id: string;
  name: string;
  /** この台帳を使う給与計算体系 */
  payrollStructureIds: string[];
  period: PaySlipLayoutPeriod;
  /** 帳票出力プロファイル（様式第20号等） */
  exportProfile: WageLedgerExportProfile;
  header: WageLedgerLayoutItem[];
  body: WageLedgerLayoutBodyGroup[];
  updatedAt: string;
}

/**
 * 給与計算体系マスタ（読み取り専用）。給与明細書用マスタと同様に参照用に別テーブルで持つ。
 */
export interface WageLedgerLayoutStructure {
  id: string;
  name: string;
  sortOrder: number;
}

// ─── 源泉徴収票マスタ（設定 > 源泉徴収票） ─────────────────────

/** 源泉徴収票のマイナンバー掲載方針 */
export type WithholdingSlipMyNumberPolicy = 'taxOfficeCopyOnly' | 'maskEmployeeCopy';

/** 源泉徴収票の列が参照するデータソース種別 */
export type WithholdingSlipFieldSourceType = 'payrollItemAggregate' | 'yearEndAdjustment' | 'employeeMaster' | 'computed';

/** 源泉徴収票の法定列（③–㉘）とデータソースのマッピング */
export interface WithholdingSlipFieldMapping {
  /** 列コード（例: paymentAmount, withholdingTax） */
  columnCode: string;
  /** 票面上の表示ラベル */
  label: string;
  sourceType: WithholdingSlipFieldSourceType;
  /** payroll item id や調整フィールド key。computed 等は null */
  sourceRef: string | null;
  required: boolean;
}

/** 支払者ブロックの上書き（事業場マスタ参照またはインライン） */
export interface WithholdingSlipPayerOverrides {
  taxOfficeCode?: string;
  recipientNumberPrefix?: string;
}

/**
 * 源泉徴収票のフィールドマッピングプロファイル。
 * 給与明細書レイアウトではなく、法定票の列をデータソースへ結び付ける定義。
 */
export interface WithholdingSlipProfile {
  id: string;
  name: string;
  period: PaySlipLayoutPeriod;
  payrollStructureIds: string[];
  payerOverrides?: WithholdingSlipPayerOverrides;
  fieldMappings: WithholdingSlipFieldMapping[];
  /** 摘要（㉘）のテンプレート行 */
  remarksTemplates: string[];
  myNumberPolicy: WithholdingSlipMyNumberPolicy;
  updatedAt: string;
}

// ─── 退職所得の源泉徴収票マスタ（設定 > 退職所得の源泉徴収票） ─────

/** 退職所得の源泉徴収票（R8 様式 番号1–7）の列マッピング */
export interface RetirementWithholdingSlipFieldMapping {
  columnCode: string;
  label: string;
  sourceType: WithholdingSlipFieldSourceType;
  sourceRef: string | null;
  required: boolean;
}
export interface RetirementWithholdingSlipProfile {
  id: string;
  name: string;
  period: PaySlipLayoutPeriod;
  payrollStructureIds: string[];
  payerOverrides?: WithholdingSlipPayerOverrides;
  fieldMappings: RetirementWithholdingSlipFieldMapping[];
  remarksTemplates: string[];
  myNumberPolicy: WithholdingSlipMyNumberPolicy;
  updatedAt: string;
}

// ─── 源泉徴収簿・住民税納入内訳（月次 ledger column profile） ─────

/** 月次帳票の列と payroll item のマッピング */
export interface MonthlyLedgerColumnMapping {
  columnCode: string;
  label: string;
  payrollItemId: string | null;
  required: boolean;
}
export interface IncomeTaxStatementProfile {
  id: string;
  name: string;
  period: PaySlipLayoutPeriod;
  payrollStructureIds: string[];
  columnMappings: MonthlyLedgerColumnMapping[];
  updatedAt: string;
}
export interface ResidentTaxStatementProfile {
  id: string;
  name: string;
  period: PaySlipLayoutPeriod;
  payrollStructureIds: string[];
  columnMappings: MonthlyLedgerColumnMapping[];
  updatedAt: string;
}

// ─── 法定調書合計表（officeYear 集計 profile） ─────────────────

export interface AggregateFieldMapping {
  fieldCode: string;
  label: string;
  payrollItemIds: string[];
  required: boolean;
}
export interface StatutoryReportSummaryProfile {
  id: string;
  name: string;
  period: PaySlipLayoutPeriod;
  payrollStructureIds: string[];
  aggregateMappings: AggregateFieldMapping[];
  updatedAt: string;
}

// ─── 給与支払報告書（総括表） ─────────────────────────────────

export interface SalaryPaymentReportMunicipalityOverrides {
  /** 特別徴収指定番号 */
  specialCollectionDesignationNumber?: string;
  /** 普通徴収理由コード */
  ordinaryCollectionReasonCode?: string;
}
export interface SalaryPaymentReportFieldMapping {
  columnCode: string;
  label: string;
  sourceType: WithholdingSlipFieldSourceType;
  sourceRef: string | null;
  required: boolean;
}
export interface SalaryPaymentReportProfile {
  id: string;
  name: string;
  period: PaySlipLayoutPeriod;
  payrollStructureIds: string[];
  municipalityOverrides?: SalaryPaymentReportMunicipalityOverrides;
  fieldMappings: SalaryPaymentReportFieldMapping[];
  updatedAt: string;
}

// ─── 労働保険 年度更新申告書 ─────────────────────────────────

export interface LaborInsuranceDeclarationProfile {
  id: string;
  name: string;
  period: PaySlipLayoutPeriod;
  payrollStructureIds: string[];
  /** 賃金総額に算入する payroll item id */
  wageItemIds: string[];
  updatedAt: string;
}

// ─── 算定基礎届 ─────────────────────────────────────────────

export type FixedWageClassification = 'fixed' | 'variable' | 'exclude';
export interface StandardRemunerationWageClassification {
  payrollItemId: string;
  classification: FixedWageClassification;
}
export interface StandardRemunerationNotificationProfile {
  id: string;
  name: string;
  period: PaySlipLayoutPeriod;
  payrollStructureIds: string[];
  wageClassifications: StandardRemunerationWageClassification[];
  updatedAt: string;
}

// ─── 法定基準一覧（Legal Welfare and Taxes > 法定基準一覧） ─────────

/** 法定基準テーブルの列。金額・率・等級のような数値列は右寄せにする */
export interface StatutoryStandardTableColumn {
  label: string;
  /** 数値・金額・率の列に付ける。既定は左寄せ */
  align?: 'end';
}

/**
 * 法定基準が返す値の対応表そのもの。
 * 照会キー（等級・都道府県・距離など）と戻り値を、表の形のまま素の文字列で持つ。
 * 表ごとに列が全く違うため、型で列を固定せず列定義と行を並べて持つ。
 *
 * 但し書きは `description`、出典は `sources` に持たせ、ここは値だけに絞る。
 */
export interface StatutoryStandardTable {
  columns: StatutoryStandardTableColumn[];
  /** 行。各行のセル数は `columns` と同じ */
  rows: string[][];
}

/** 法定基準の根拠を確認できる関連ソース（法令・官公庁のページなど） */
export interface StatutoryStandardSource {
  label: string;
  /** http / https のみ。画面から編集できるため描画前に検証する */
  url: string;
}

/**
 * 法定基準 1 件。
 * 給与計算が金額・率・等級を法令に求める先（料額表・税額表）の定義。
 * 有効期間は帳票マスタと同じ `PaySlipLayoutPeriod` を使う。
 *
 * 制定根拠（法定 / 社内）のフィールドは持たない。社内管理の表は参照先カタログ
 * （`PayrollReferenceCatalogEntry`）が `coreSource: 'in-house'` で持つため、
 * 法定基準一覧に載る時点で全件が法定であり、軸として意味を成さないため。
 */
export interface StatutoryStandardEntry {
  id: string;
  name: string;
  /**
   * 1 行目に「その表に何が入っているか」（照会キー・戻り値・粒度）、2 行目以降に
   * 適用時期・端数処理・抜粋である旨などの但し書きを改行区切りで持つ。詳細画面の
   * ヘッダに出るのは 1 行目だけなので、1 行目だけで表の中身が分かるように書く。
   * 管轄プロダクト名は利用者向けの文言に出さない。
   */
  description: string;
  /**
   * 対応する `valueType: 'table'` の payroll item の `id`（`pi-i005` 通勤費 非課税限度額表 等）。
   * 法定基準 1 件と payroll item が 1 対 1 なので、参照先カタログ
   * （`PayrollReferenceCatalogEntry.payrollItemId`）と同じく `code` ではなく `id` で名指しする。
   *
   * 対応先が決まっていない行は空文字を持つ。`string | null` に広げないのは、読み側すべてに
   * 分岐が増えるうえ「未定」と「解決できなかった」を画面が区別できなくなるため
   * （参照先カタログと同じ扱い）。
   */
  payrollItemId: string;
  period: PaySlipLayoutPeriod;
  /** 根拠を確認できる関連ソース。法令・官公庁のページを指す */
  sources: StatutoryStandardSource[];
  /**
   * 法定基準が返す値の対応表。法定の表は Country Plugin・自治体通知が配るため画面から
   * 編集させず、テーブルタブで読み取り専用に見せる。
   */
  table: StatutoryStandardTable;
  /** 一覧の既定並び順 */
  sortOrder: number;
  updatedAt: string;
}

// ─── 給与計算参照先カタログ ─────────────────────────────────

/**
 * 参照テーブルの列。法定基準（`StatutoryStandardTableColumn`）と違い、
 * 「この列が payroll item のどれで、その値がコア DB のどこにあるか」を列自身に持たせる。
 *
 * コア DB 抽出（`coreSource` が people / attendances / contracts）の本体は値そのものでは
 * なく「どの情報項目をどこから読んでいるか」であり、列と項目が 1 対 1 で対応する。
 * 法定基準は等級・都道府県などの照会キーで引く表なので、列に在り処を持たせる意味がない。
 * 社内マスタ（`coreSource: 'in-house'`）も同じく照会キーで引く表なので、両フィールドは
 * 持たずに列ラベルだけを持つ。
 */
export interface PayrollReferenceCatalogTableColumn {
  label: string;
  /** 数値・金額・時間の列に付ける。既定は左寄せ */
  align?: 'end';
  /**
   * 対応する payroll item の `id`（例 `pi-r020`）。表示用の `code`（`R020`）ではなく
   * `id` で持つのは、`code` が利用者の編集対象で永続データの参照キーにできないため。
   * 氏名・社員番号のような行の識別列と、雇用契約 DB 未実装のモック列は持たない。
   */
  payrollItemId?: string;
  /**
   * コア DB 上の項目の在り処。給与計算入力スナップショットと**表記法**を揃える
   * （`›` 区切りで 根 › カテゴリ › 項目 の 3 階層。例 `メンバー › 基本情報 › 生年月日`）。
   *
   * 揃えるのは形式までで、**文字列が全列でスナップショットと一致するわけではない**。
   * 参照先は `coreSource` のコア DB 単位で凍結するため、スナップショットが別の根
   * （`福利厚生 › …` 等）から引いている項目は根を付け替えてある。また payroll item
   * カタログにしか無い供給元（`申告データ › …`）は参照先側で新設している。
   * どの列がどれに当たるかは各テーブルファイルの JSDoc に列挙してある。
   *
   * 雇用契約 DB 未実装のモック列は在り処が無いため持たない。
   */
  sourcePath?: string;
}

/**
 * 参照先が返す値そのもの。コア DB 抽出は 行 = worker・列 = コア DB の項目、
 * 社内マスタ（`coreSource: 'in-house'`）は 行 = 照会キー・列 = 戻り値になる。
 * 表ごとに列が全く違うため、型で列を固定せず列定義と行を並べて持つ。
 *
 * 但し書き（どこから凍結するか・モック値である旨など）は参照先の
 * `description` に持たせ、ここは値だけに絞る。
 */
export interface PayrollReferenceCatalogTable {
  columns: PayrollReferenceCatalogTableColumn[];
  /** 行。各行のセル数は `columns` と同じ。行の並びはコア DB 抽出 3 表で揃える */
  rows: string[][];
}

/**
 * 参照先が読む先。給与計算基準日にここから値を凍結する。
 *
 * 前 3 つは DRESS CODE のコア DB で、行 = worker の抽出テーブルになる。
 * `in-house` はコア DB ではなく、テナントが自社の規程で定める社内マスタ
 * （技術手当テーブル・号俸表・役職手当単価表 等）と、社外 SaaS から取り込む
 * 運用データを指す。法令が定める表は参照先ではなく法定基準一覧
 * （`StatutoryStandardEntry`）が持つため、ここには来ない。
 */
export type PayrollReferenceCatalogCoreSource = 'people' | 'attendances' | 'contracts' | 'in-house';

/**
 * 給与計算参照先カタログ 1 件。
 * 給与計算が「手元にある確定した情報」を読み解く先の定義。読み先はコア DB の
 * 抽出テーブルか、テナントが自社の規程で定める社内マスタのどちらか。
 * 有効期間は帳票マスタと同じ `PaySlipLayoutPeriod` を使う。
 *
 * 法定基準（`StatutoryStandardEntry`）との差:
 * - `sources`（外部 URL の関連ソース）は持たない。参照先の出典は社外の法令ではなく
 *   DRESS CODE のコア DB か顧客の社内規程であり、それは `coreSource` と列の
 *   `sourcePath` が表す。法令を出典に持つ表は法定基準一覧側に載る。
 * - 制定根拠の軸は持たない。参照先は全件が社内で確定した情報であり、法定 / 社内の
 *   分岐は「法定基準一覧に載るか参照先カタログに載るか」そのものになったため。
 */
export interface PayrollReferenceCatalogEntry {
  id: string;
  name: string;
  /**
   * 参照できる内容に続けて、どこから給与計算基準日で凍結するか・モック値である旨・
   * 未実装項目を含む旨などの但し書きを改行区切りで持つ。
   */
  description: string;
  /**
   * 対応する `valueType: 'table'` の payroll item の `id`
   * （`pi-r048` メンバー / `pi-r049` 勤怠 / `pi-r051` 契約 / 社内マスタは `pi-i0xx`）。
   * 参照先 1 件と payroll item が 1 対 1 なので、明細系マスタと同じく `code` ではなく
   * `id` で名指しする。
   */
  payrollItemId: string;
  /** 読み先。一覧・詳細でどのコア DB の抽出か、社内マスタかを示す */
  coreSource: PayrollReferenceCatalogCoreSource;
  period: PaySlipLayoutPeriod;
  /**
   * 参照先が返す値。コア DB から抽出した結果・顧客が規程に沿って登録した表なので
   * 画面から編集させず、テーブルタブで読み取り専用に見せる。
   */
  table: PayrollReferenceCatalogTable;
  /** 一覧の既定並び順（メンバー → 勤怠 → 契約 → 社内マスタ） */
  sortOrder: number;
  updatedAt: string;
}

/** TODO(attendance-data): 実データ未定。Payroll 側と同形の空カタログ。 */
export type AttendanceReferenceCatalogEntry = PayrollReferenceCatalogEntry;

// ─── テナント専用帳票マスタ（設定 > テナント専用帳票） ─────────────

/**
 * 宛先ブロックの項目が指す固定フィールド。
 * テナント専用帳票でも「誰の・いつの分か」は payroll item ではなく帳票そのものが持つ
 * 情報なので、給与明細書と同じ軸で持つ。
 */
export type InHouseReportLayoutHeaderField = 'employeeName' | 'employeeCode' | 'department' | 'calculationPeriod' | 'paymentDate' | 'establishment';

/** テナント専用帳票に出す 1 項目。印字名は payroll item の正式名と独立に持つ */
export interface InHouseReportLayoutItem {
  id: string;
  /** 帳票に印字する項目名 */
  label: string;
  /** 紐づける payroll item の id。null は payroll item を持たない項目 */
  payrollItemId: string | null;
  /** 宛先ブロックの固定フィールドを指す項目のみ設定される */
  headerField?: InHouseReportLayoutHeaderField;
}

/**
 * 明細のグループ。給与明細書の `PaySlipLayoutBodyGroup` と違い区分 enum を持たず、
 * 見出しをテナントが自由に付ける（オリジナルの帳票を組むための可変グループ）。
 * 並びは配列順がそのまま帳票の並びになる。
 */
export interface InHouseReportLayoutBodyGroup {
  id: string;
  /** 帳票に印字するグループ見出し */
  label: string;
  items: InHouseReportLayoutItem[];
}

/**
 * テナント専用帳票のマスタ定義。法定帳票と違い様式が決まっていないため、
 * グループ・項目とも定義した通りに印字する（必須列の検証を持たない）。
 * 有効期間の型は帳票マスタ共通の `PaySlipLayoutPeriod` を使う。
 */
export interface InHouseReportLayout {
  id: string;
  name: string;
  period: PaySlipLayoutPeriod;
  /** 明細の外に出す項目（宛先・計算期間・支給日など） */
  header: InHouseReportLayoutItem[];
  body: InHouseReportLayoutBodyGroup[];
  updatedAt: string;
}